import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useLocation } from "wouter";
import { cn, formatPhoneNumber, normalizeSearchStr, formatLocalDate } from "@/lib/utils";
import { describeSendError } from "@/lib/send-guard";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileFilterSheet, MobileFilterField, MobileRegionPicker } from "@/components/mobile/mobile-filter-sheet";
import type { UserProfile } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { useColumnOrder } from "@/hooks/use-column-order";
import { SavedViewsTabStrip } from "@/components/saved-views-tab-strip";
import { LeadContactsSection } from "@/components/spreadsheet/lead-contacts-section";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TutorialHint } from "@/components/tutorial-hint";
import { SpreadsheetCell } from "./spreadsheet-cell";
import { XCircle, Star, Calendar, Plus, Loader2, Settings, Trash2, Pencil, Check, X, Undo2, Redo2, ChevronDown, Mail, Send, Edit, ArrowUpDown, ArrowUp, ArrowDown, Clock, RotateCcw, Phone, History, Bell, Eye, GripVertical, ExternalLink, HelpCircle, Building2, Megaphone, Sparkles } from "lucide-react";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import { CatchMeUpButton } from "@/components/catch-me-up-button";
import { LeadCatcherConversationCard } from "@/components/lead-catcher-conversation-card";
import { QualificationBreakdownDialog, LeadQualificationCard, qualificationLabelColor } from "@/components/qualification-breakdown-dialog";
import { LeadOutboundCallCard } from "@/components/leads/lead-outbound-call-card";
import { LeadCampaignDialogs } from "@/components/leads/lead-campaign-dialogs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { VoiceToTextTextarea as Textarea, VoiceToTextButton } from "@/components/voice-field";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { EmailVariablesSelector } from "@/components/email-variables-selector";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { CcEmailInput } from "@/components/cc-email-input";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import type { Lead, Region, ClientTypeRecord, Talent } from "@shared/schema";
import { ExpandableText } from "@/components/ui/expandable-text";
import { AddLeadModal, type AddLeadFormValues } from "./add-lead-modal";
import { initials as bsInitials, avatarSwatch as bsAvatarSwatch } from "@/components/backstage/lib";

export const LEAD_EXPORT_FIELDS = [
  { key: "id", label: "Lead ID" },
  { key: "starred", label: "Starred" },
  { key: "leadName", label: "Account Name" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "emailAddress", label: "Email Address" },
  { key: "contactName", label: "Contact Name" },
  { key: "lastContacted", label: "Last Contacted" },
  { key: "notes", label: "Notes" },
  { key: "regionId", label: "Region ID" },
  { key: "regionName", label: "Region (Name)" },
  { key: "clientType", label: "Client Type" },
  { key: "venueType", label: "Venue Type" },
  { key: "venueCapacity", label: "Venue Capacity" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zipCode", label: "Zip Code" },
  { key: "rating", label: "Rating" },
  { key: "website", label: "Website" },
  { key: "talentId", label: "Talent ID" },
  { key: "rep", label: "Rep" },
  { key: "status", label: "Status" },
  { key: "emailOptedOut", label: "Email Opted Out" },
];

interface LogCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onSave: (notes: string) => void;
  isPending: boolean;
}

function LogCallDialog({ open, onOpenChange, title, onSave, isPending }: LogCallDialogProps) {
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) setNotes("");
  }, [open]);

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="callNotes">Call Notes</Label>
            <Textarea
              id="callNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe the call..."
              rows={6}
              data-testid="input-call-notes"
            />
          </div>
          <Button
            onClick={() => { if (notes.trim()) onSave(notes); }}
            disabled={!notes.trim() || isPending}
            className="w-full"
            data-testid="button-save-call"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Phone className="w-4 h-4 mr-2" />
            )}
            Save Call Log
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const QUADRANT_OPTIONS = ["NE", "NW", "SE", "SW"] as const;
export type QuadrantCode = typeof QUADRANT_OPTIONS[number];

interface LeadsSpreadsheetProps {
  userId: string;
  isAdmin?: boolean;
  searchQuery?: string;
  quadrantFilter?: Set<string>;
  // When set (e.g. via `?openLead=<id>` from the sidebar quick-find),
  // selects the matching row and opens the View Details dialog as
  // soon as the lead is present in the loaded list. Cleared via
  // `onAutoOpenComplete` so the URL doesn't keep re-triggering it.
  autoOpenLeadId?: string;
  onAutoOpenComplete?: () => void;
  // Smart-search filter spec (Task #120). Merged with the manual
  // toolbar filters when constructing the API request — manual
  // selections take precedence so a stale spec can never override an
  // active toolbar filter.
  smartFilters?: import("@shared/schema").NlFilterSpec | null;
  /** Content rendered to the right of the saved-view tabs (search bar, geotags, etc.). */
  savedViewsRightSlot?: React.ReactNode;
}

type UndoAction = 
  | { type: "edit"; leadId: string; field: string; oldValue: any; newValue: any }
  | { type: "delete"; lead: Lead }
  | { type: "bulkDelete"; leadIds: string[] };

interface RepUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

const getColumns = (clientTypeOptions: string[], isAdmin: boolean, hasMultipleTalent: boolean) => [
  { key: "starred", label: "★", width: "52px", type: "starred" },
  { key: "leadName", label: "Account Name", width: "180px" },
  { key: "phoneNumber", label: "Phone Number", width: "140px", type: "phone" },
  { key: "emailAddress", label: "Email Address", width: "200px" },
  { key: "contactName", label: "Contact Name", width: "160px" },
  { key: "lastContacted", label: "Last Contacted", width: "140px" },
  { key: "notes", label: "Notes", width: "250px" },
  { key: "rating", label: "Rating", width: "120px", type: "rating" },
  { key: "website", label: "Website", width: "200px", type: "url" },
  { key: "address", label: "Address", width: "200px" },
  { key: "city", label: "City", width: "140px" },
  { key: "state", label: "State", width: "80px" },
  { key: "zipCode", label: "Zip", width: "90px" },
  { key: "clientType", label: "Client Type", width: "130px", type: "select", options: clientTypeOptions },
  { key: "regionId", label: "Region", width: "150px", type: "region" },
  { key: "talentId", label: "Talent", width: "140px", type: "talent" },
  { key: "rep", label: "Rep", width: "140px", type: isAdmin ? "user-select" : "readonly" },
  { key: "venueType", label: "Venue Type", width: "150px" },
  { key: "venueCapacity", label: "Venue Capacity", width: "120px" },
  { key: "emailOptedOut", label: "Opt Out", width: "80px", type: "checkbox" },
];

export function LeadsSpreadsheet({ userId, isAdmin: isAdminProp, searchQuery = "", quadrantFilter, autoOpenLeadId, onAutoOpenComplete, smartFilters = null, savedViewsRightSlot }: LeadsSpreadsheetProps) {
  const isMobile = useIsMobile();
  const [, navigate] = useLocation();
  const [selectedRegions, setSelectedRegions] = useState<string[]>(() => {
    // Load saved regions from localStorage
    const saved = localStorage.getItem("leads-selected-regions");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return []; // Empty array means "all regions"
  });
  const [selectedClientType, setSelectedClientType] = useState<string>(() => {
    // Load saved client type from localStorage.
    // null means "never been set" → sentinel so default logic can apply DB preference.
    // "" means user explicitly chose "All Client Types".
    const saved = localStorage.getItem("leads-selected-client-type");
    return saved !== null ? saved : "__unset__";
  });
  // Task #324 — when the user has no saved talent preference we start in an
  // "__unset__" sentinel state and resolve it to the workspace default talent
  // (or the lone talent in single-talent workspaces) once the talent list
  // loads. An explicit user choice is tracked via talentPrefExplicitRef so an
  // auto-resolved default is never persisted as if the user picked it.
  const [selectedTalentId, setSelectedTalentId] = useState<string>(() => {
    const saved = localStorage.getItem("leads-selected-talent");
    // Treat a saved "all" as unset so default-talent resolution re-runs each
    // session. A specific talent ID is still honoured as a sticky preference.
    return saved !== null && saved !== "all" ? saved : "__unset__";
  });
  const talentPrefExplicitRef = useRef<boolean>(
    (() => {
      const saved = localStorage.getItem("leads-selected-talent");
      return saved !== null && saved !== "all";
    })(),
  );
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowId: string; column: string } | null>(null);
  const [newRegionName, setNewRegionName] = useState("");
  const [regionNameError, setRegionNameError] = useState<string | null>(null);
  const [regionNameWarning, setRegionNameWarning] = useState<string | null>(null);
  const [regionSearch, setRegionSearch] = useState("");
  const [isRegionDialogOpen, setIsRegionDialogOpen] = useState(false);
  const [isManageRegionsOpen, setIsManageRegionsOpen] = useState(false);
  const [manageRegionSearch, setManageRegionSearch] = useState("");
  const [editingRegionId, setEditingRegionId] = useState<string | null>(null);
  const [editingRegionName, setEditingRegionName] = useState("");
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoAction[]>([]);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isLeadEmailScheduleOpen, setIsLeadEmailScheduleOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<StagedAttachment[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [isLogCallDialogOpen, setIsLogCallDialogOpen] = useState(false);
  const [editNotesLeadId, setEditNotesLeadId] = useState<string | null>(null);
  const [editNotesValue, setEditNotesValue] = useState("");
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  const [editingActivityNotes, setEditingActivityNotes] = useState("");
  const [isViewDetailsDialogOpen, setIsViewDetailsDialogOpen] = useState(false);
  const [isSetReminderDialogOpen, setIsSetReminderDialogOpen] = useState(false);
  const [isMobileActionSheetOpen, setIsMobileActionSheetOpen] = useState(false);
  const [isMobileEditOpen, setIsMobileEditOpen] = useState(false);
  const [mobileEditForm, setMobileEditForm] = useState<{leadName: string; contactName: string; phoneNumber: string; emailAddress: string; notes: string}>({ leadName: "", contactName: "", phoneNumber: "", emailAddress: "", notes: "" });
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [visibleRowCount, setVisibleRowCount] = useState(50);
  const [visibleLeadsCount, setVisibleLeadsCount] = useState(50);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isCampaignChooserOpen, setIsCampaignChooserOpen] = useState(false);
  const [isEnrichOpen, setIsEnrichOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [confirmDeleteLeadId, setConfirmDeleteLeadId] = useState<string | null>(null);
  // Qualification breakdown popup — leadId or null when closed.
  const [qualificationLeadId, setQualificationLeadId] = useState<string | null>(null);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [pendingLeadCreate, setPendingLeadCreate] = useState<{ regionId: string; clientType: string; fieldKey: string; value: string } | null>(null);
  const [duplicateMatchedLeads, setDuplicateMatchedLeads] = useState<Array<{ id: string; leadName: string | null; emailAddress: string | null; phoneNumber: string | null; createdBy: string; matchedFields: string[] }>>([]);
  const [selectedMergeTargetId, setSelectedMergeTargetId] = useState<string>("");
  const [clientDuplicateOpen, setClientDuplicateOpen] = useState(false);
  const [matchedClients, setMatchedClients] = useState<Array<{ id: string; accountName: string | null; phoneNumber: string | null; emailAddress: string | null; matchedFields: string[] }>>([]);
  const [pendingLeadUpdate, setPendingLeadUpdate] = useState<{ leadId: string; field: string; value: string | boolean } | null>(null);
  const [regionDeleteError, setRegionDeleteError] = useState<{
    regionId: string;
    regionName: string;
    message: string;
    leadCount: number;
    clientCount: number;
    invoiceCount: number;
    leads: { id: string; accountName: string | null }[];
    clients: { id: string; accountName: string | null }[];
    invoices: { id: string; invoiceNumber: string; accountName: string | null }[];
  } | null>(null);
  const [reassignTargetRegionId, setReassignTargetRegionId] = useState<string>("");
  const [selectedMergeIds, setSelectedMergeIds] = useState<Set<string>>(new Set());
  const [isMergeConfirmOpen, setIsMergeConfirmOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string>("");
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [dragOverLeadId, setDragOverLeadId] = useState<string | null>(null);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [pendingModalLeadData, setPendingModalLeadData] = useState<AddLeadFormValues | null>(null);
  const { toast } = useToast();
  const queryClientHook = useQueryClient();

  // Save selected regions to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("leads-selected-regions", JSON.stringify(selectedRegions));
  }, [selectedRegions]);

  // Save selected client type to localStorage when it changes (never save the sentinel)
  useEffect(() => {
    if (selectedClientType !== "__unset__") {
      localStorage.setItem("leads-selected-client-type", selectedClientType);
    }
  }, [selectedClientType]);

  // Save selected talent to localStorage only when it reflects an explicit
  // user choice (never the "__unset__" sentinel or an auto-resolved default).
  useEffect(() => {
    if (selectedTalentId === "__unset__") return;
    if (!talentPrefExplicitRef.current) return;
    localStorage.setItem("leads-selected-talent", selectedTalentId);
  }, [selectedTalentId]);

  // The infinite query refetches from page 1 whenever any of its key
  // dimensions (selectedRegions, sort, filters, debounced search) change,
  // so we no longer need a manual "reset visible count" effect tied to
  // selectedRegions — the queryKey diff handles it. The IntersectionObserver
  // that drives fetchNextPage lives further down, after the infinite query
  // hook is declared.

  const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"];

  const validateRegionFormat = (name: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return "Region name is required";
    
    const match = trimmed.match(/^(.+),\s*([A-Za-z]{2})$/);
    if (!match) return "Format must be: City, ST (e.g., Columbus, OH)";
    
    const stateCode = match[2].toUpperCase();
    if (!US_STATES.includes(stateCode)) return `"${stateCode}" is not a valid US state code`;
    
    return null;
  };

  const { data: regions = [], isLoading: regionsLoading } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const { data: defaultClientType } = useQuery<ClientTypeRecord | null>({
    queryKey: ["/api/client-types/default"],
  });

  const { data: sandbox } = useQuery<{ settings?: { quadrantTagsEnabled?: boolean } }>({
    queryKey: ["/api/lead-gen/sandbox"],
    staleTime: 60_000,
  });
  const quadrantTagsEnabled = sandbox?.settings?.quadrantTagsEnabled !== false;

  const { data: invalidEmailsData = [] } = useQuery<{ email: string; result: string }[]>({
    queryKey: ["/api/email-verifications"],
    staleTime: 5 * 60 * 1000, // 5 min — doesn't need to be real-time
  });
  const invalidEmailSet = useMemo(
    () => new Set(invalidEmailsData.map((e) => e.email.toLowerCase())),
    [invalidEmailsData],
  );

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });
  const isAdmin = isAdminProp !== undefined ? isAdminProp : profile?.role === "admin";
  // Bulk Edit / Bulk Delete are gated behind this permission key so admins can
  // authorize specific roles/users via the Users & Permissions UI. Selection
  // itself is open to everyone (see checkbox rendering below).
  const { can } = useEffectivePermissions();
  const canBulkActions = can("leads.feature.bulkActions");
  const { data: smsStatusData } = useQuery<{ smsEnabled: boolean }>({
    queryKey: ["/api/workspace/sms-status"],
  });
  const twilioEnabled = !!smsStatusData?.smsEnabled;

  // Per-user client-type restriction. Admin/billing always see everything.
  const userAllowedClientTypeIds: string[] = useMemo(
    () => ((profile as any)?.allowedClientTypeIds as string[] | undefined) || [],
    [profile]
  );
  const userIsRestrictedByType =
    userAllowedClientTypeIds.length > 0 && profile?.role !== "admin" && profile?.role !== "billing";

  // Set default client type if not saved in localStorage, or if saved type is invalid
  useEffect(() => {
    if (clientTypes.length === 0) return;
    const allowedNames = userIsRestrictedByType
      ? new Set(clientTypes.filter(ct => userAllowedClientTypeIds.includes(ct.id)).map(ct => ct.name))
      : null;
    const isAllowedName = (n: string) => !allowedNames || allowedNames.has(n);

    const isAllSelected = selectedClientType === "";
    const isUnset = selectedClientType === "__unset__";
    const currentValid =
      (isAllSelected && !userIsRestrictedByType) ||
      (!isUnset && clientTypes.some(ct => ct.name === selectedClientType && ct.isActive) && isAllowedName(selectedClientType));

    if (currentValid) return;

    // Restricted user: force first allowed type (never "All")
    if (userIsRestrictedByType) {
      const firstAllowed = clientTypes.find(ct => ct.isActive && userAllowedClientTypeIds.includes(ct.id));
      if (firstAllowed) {
        setSelectedClientType(firstAllowed.name);
        localStorage.setItem("leads-selected-client-type", firstAllowed.name);
      }
      return;
    }

    // Unrestricted: prefer DB default, else "All"
    if (defaultClientType?.name && defaultClientType.isActive) {
      setSelectedClientType(defaultClientType.name);
      localStorage.setItem("leads-selected-client-type", defaultClientType.name);
    } else {
      setSelectedClientType("");
      localStorage.setItem("leads-selected-client-type", "");
    }
  }, [selectedClientType, defaultClientType, clientTypes, userIsRestrictedByType, userAllowedClientTypeIds]);

  const clientTypeOptions = useMemo(() => {
    let list = clientTypes.filter(ct => ct.isActive);
    if (userIsRestrictedByType) {
      list = list.filter(ct => userAllowedClientTypeIds.includes(ct.id));
    }
    return list
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(ct => ct.name);
  }, [clientTypes, userIsRestrictedByType, userAllowedClientTypeIds]);

  const { data: users = [] } = useQuery<RepUser[]>({
    queryKey: ["/api/users"],
    enabled: isAdmin,
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  // Task #324 — resolve the "__unset__" sentinel once talent loads: a lone
  // talent auto-selects (single-talent workspaces never make the user pick),
  // otherwise fall back to the workspace default talent, and finally "all".
  useEffect(() => {
    if (selectedTalentId !== "__unset__") return;
    if (talent.length === 0) return;
    const activeTalent = talent.filter((t) => t.isActive);
    if (activeTalent.length === 1) {
      setSelectedTalentId(activeTalent[0].id);
      return;
    }
    const def = activeTalent.find((t) => t.isDefault) || talent.find((t) => t.isDefault);
    setSelectedTalentId(def ? def.id : "all");
  }, [selectedTalentId, talent]);

  interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    body: string;
    bodyRich?: string | null;
    hiddenFromPickers?: boolean;
  }

  const { data: emailTemplates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: isEmailDialogOpen,
  });

  const hasMultipleTalent = talent.filter(e => e.isActive).length > 1;

  // Effective hidden columns: when the user has customized their own
  // (`leadColumnsCustomized = true`), their personal array wins. Otherwise we
  // fall back to the admin-set workspace defaults. See Admin → Sales → Lead
  // Settings for the workspace defaults UI.
  const { data: workspaceDefaults } = useQuery<{ defaultHiddenLeadColumns: string[] }>({
    queryKey: ["/api/workspace/lead-column-defaults"],
    staleTime: 60_000,
  });
  const hiddenLeadColumns: string[] = useMemo(() => {
    const customized = (profile as any)?.leadColumnsCustomized === true;
    if (customized) {
      return ((profile as any)?.hiddenLeadColumns as string[] | undefined) || [];
    }
    return workspaceDefaults?.defaultHiddenLeadColumns ?? [];
  }, [profile, workspaceDefaults]);
  const VISIBLE_COLUMNS = useMemo(() => {
    const all = getColumns(clientTypeOptions, isAdmin, hasMultipleTalent);
    if (hiddenLeadColumns.length === 0) return all;
    const hidden = new Set(hiddenLeadColumns);
    // leadName is always required and cannot be hidden
    return all.filter((c) => c.key === "leadName" || !hidden.has(c.key));
  }, [clientTypeOptions, isAdmin, hasMultipleTalent, hiddenLeadColumns]);

  const visibleColumnKeys = useMemo(() => VISIBLE_COLUMNS.map((c) => c.key), [VISIBLE_COLUMNS]);
  const { orderedKeys, moveColumn } = useColumnOrder({
    storageKey: "leads-column-order",
    columnKeys: visibleColumnKeys,
  });
  const COLUMNS = useMemo(() => {
    const map = new Map(VISIBLE_COLUMNS.map((c) => [c.key, c] as const));
    return orderedKeys.map((k) => map.get(k)!).filter(Boolean);
  }, [orderedKeys, VISIBLE_COLUMNS]);
  const [draggedColKey, setDraggedColKey] = useState<string | null>(null);
  const [dragOverColKey, setDragOverColKey] = useState<string | null>(null);

  const { getColumnWidth, handleResizeStart } = useResizableColumns({
    columns: COLUMNS,
    storageKey: "leads-column-widths",
  });

  // Build name → id map for clientTypes so we can compare lead.clientType (name) against region.clientTypeIds
  const clientTypeNameToId = useMemo(() => {
    const m = new Map<string, string>();
    for (const ct of clientTypes) m.set(ct.name, ct.id);
    return m;
  }, [clientTypes]);

  // Helper: regions allowed for a given client type name (or all regions if no clientType / unknown)
  const regionsForClientType = (clientTypeName?: string | null) => {
    if (!clientTypeName) return regions;
    const ctId = clientTypeNameToId.get(clientTypeName);
    if (!ctId) return regions;
    return regions.filter((r) => {
      const ids = (r as any).clientTypeIds as string[] | null | undefined;
      return !ids || ids.length === 0 || ids.includes(ctId);
    });
  };

  // T106: when a Client Type filter is active, narrow the region filter dropdown to allowed regions only
  const clientTypeFilteredRegions = useMemo(() => {
    if (!selectedClientType || selectedClientType === "__unset__") return regions;
    return regionsForClientType(selectedClientType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, selectedClientType, clientTypeNameToId]);

  const filteredRegions = useMemo(() => {
    const base = clientTypeFilteredRegions;
    if (!regionSearch.trim()) return base;
    const search = normalizeSearchStr(regionSearch);
    return base.filter(r => normalizeSearchStr(r.name).includes(search));
  }, [clientTypeFilteredRegions, regionSearch]);

  const filteredManageRegions = useMemo(() => {
    if (!manageRegionSearch.trim()) return regions;
    const search = normalizeSearchStr(manageRegionSearch);
    return regions.filter(r => normalizeSearchStr(r.name).includes(search));
  }, [regions, manageRegionSearch]);

  // Server-side sort name. UI columns map onto the storage whitelist
  // (`server/storage.ts` LeadsSortName). When `sortField` is null we send
  // sort=default which uses the status-priority + sortOrder ranking.
  const SERVER_SORTABLE = new Set([
    "leadName",
    "contactName",
    "rating",
    "lastContacted",
    "quadrant",
  ]);
  const serverSort = sortField && SERVER_SORTABLE.has(sortField) ? sortField : "default";
  const serverDir: "asc" | "desc" = sortField ? sortDirection : "desc";

  // Debounce search keystrokes so we don't hammer /api/leads on every char.
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const leadsQueryKey = useMemo(
    () => [
      "/api/leads",
      {
        regions: [...selectedRegions].sort().join(","),
        sort: serverSort,
        dir: serverDir,
        quadrants:
          quadrantFilter && quadrantFilter.size > 0
            ? Array.from(quadrantFilter).sort().join(",")
            : null,
        q: debouncedSearch.trim() || null,
        clientType:
          selectedClientType && selectedClientType !== "__unset__"
            ? selectedClientType
            : null,
        talentId:
          selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
            ? selectedTalentId
            : null,
        // Stringify the smart spec into the cache key so a parsed
        // sentence triggers a fresh fetch.
        smart: smartFilters ? JSON.stringify(smartFilters) : null,
      },
    ] as const,
    [
      selectedRegions,
      serverSort,
      serverDir,
      quadrantFilter,
      debouncedSearch,
      selectedClientType,
      selectedTalentId,
      smartFilters,
    ],
  );

  const {
    data: leadsPages,
    isLoading: leadsLoading,
    isError: leadsIsError,
    refetch: refetchLeads,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<{ items: Lead[]; nextCursor: string | null; total?: number }>({
    queryKey: leadsQueryKey,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "100");
      params.set("skipTotal", "1");
      if (pageParam) params.set("cursor", pageParam as string);
      params.set("sort", serverSort);
      params.set("dir", serverDir);
      if (selectedRegions.length > 0) {
        params.set("regionIds", selectedRegions.join(","));
      }
      if (quadrantFilter && quadrantFilter.size > 0) {
        params.set("quadrants", Array.from(quadrantFilter).join(","));
      }
      const q = debouncedSearch.trim();
      if (q) params.set("q", q);
      if (selectedClientType && selectedClientType !== "__unset__") {
        params.set("clientType", selectedClientType);
      }
      if (selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__") {
        params.set("talentId", selectedTalentId);
      }
      // Smart-search additions. Manual selections (set above) win, so
      // these only apply when the toolbar hasn't already chosen one.
      if (smartFilters) {
        if (smartFilters.regionIds && smartFilters.regionIds.length > 0 && selectedRegions.length === 0) {
          params.set("regionIds", smartFilters.regionIds.join(","));
        }
        if (smartFilters.clientType && !params.has("clientType")) {
          params.set("clientType", smartFilters.clientType);
        }
        if (smartFilters.talentId && !params.has("talentId")) {
          params.set("talentId", smartFilters.talentId);
        }
        if (smartFilters.status && !params.has("status")) {
          params.set("status", smartFilters.status);
        }
        if (smartFilters.quadrants && smartFilters.quadrants.length > 0 && !params.has("quadrants")) {
          params.set("quadrants", smartFilters.quadrants.join(","));
        }
        if (typeof smartFilters.ratingMin === "number") {
          params.set("ratingMin", String(smartFilters.ratingMin));
        }
        if (typeof smartFilters.lastContactedDaysAgo === "number") {
          params.set("lastContactedDaysAgo", String(smartFilters.lastContactedDaysAgo));
        }
        if (smartFilters.venueType) {
          params.set("venueType", smartFilters.venueType);
        }
      }
      const response = await fetch(`/api/leads?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch leads");
      return response.json();
    },
  });

  const leads = useMemo<Lead[]>(
    () => (leadsPages?.pages ?? []).flatMap((p) => p.items),
    [leadsPages],
  );

  useEffect(() => {
    if (leadsIsError) window.dispatchEvent(new CustomEvent("clarik:query-failure"));
  }, [leadsIsError]);

  // Auto-open a lead's View Details dialog when navigating in via
  // `?openLead=<id>` (e.g. from the sidebar quick-find). Guarded by a
  // ref so we only fire it once per navigation; the parent page clears
  // the URL via `onAutoOpenComplete` once we've opened the dialog.
  const autoOpenLeadHandledRef = useRef(false);
  useEffect(() => {
    if (!autoOpenLeadId) {
      autoOpenLeadHandledRef.current = false;
      return;
    }
    if (autoOpenLeadHandledRef.current || leads.length === 0) return;
    const lead = leads.find((l) => l.id === autoOpenLeadId);
    if (lead) {
      autoOpenLeadHandledRef.current = true;
      setSelectedRowId(autoOpenLeadId);
      setIsViewDetailsDialogOpen(true);
      onAutoOpenComplete?.();
    }
  }, [autoOpenLeadId, leads, onAutoOpenComplete]);

  // Scroll container ref shared by the mobile + desktop branches so the
  // row virtualizer can attach to it when desktop renders.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: leads.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 28, // h-7 row height
    overscan: 10,
  });

  const { data: allClients = [] } = useQuery<Array<{ id: string; accountName: string | null; phoneNumber: string | null; mobilePhone: string | null; emailAddress: string | null }>>({
    queryKey: ["/api/clients", "lookup"],
  });

  const createRegionMutation = useMutation({
    mutationFn: async (name: string) => {
      // Default new region to current Client Type filter so it only shows up
      // for that type. "All Client Types" (empty) → unrestricted.
      const body: Record<string, unknown> = { name, createdBy: userId };
      if (selectedClientType && selectedClientType !== "" && selectedClientType !== "__unset__") {
        const ctId = clientTypeNameToId.get(selectedClientType);
        if (ctId) body.clientTypeIds = [ctId];
      }
      return apiRequest("POST", "/api/regions", body);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/regions"] });
      setNewRegionName("");
      setRegionNameWarning(null);
      setIsRegionDialogOpen(false);
      const scoped = selectedClientType && selectedClientType !== "" && selectedClientType !== "__unset__";
      toast({
        title: "Region created",
        description: scoped
          ? `New region added — only visible for "${selectedClientType}". Edit in Region Settings to change.`
          : "New region has been added.",
      });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create region.", variant: "destructive" });
    },
  });

  const updateRegionMutation = useMutation({
    mutationFn: async ({ id, name, clientTypeIds }: { id: string; name?: string; clientTypeIds?: string[] }) => {
      const body: Record<string, unknown> = {};
      if (name !== undefined) body.name = name;
      if (clientTypeIds !== undefined) body.clientTypeIds = clientTypeIds;
      const response = await apiRequest("PATCH", `/api/regions/${id}`, body);
      return response.json();
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/regions"] });
      setEditingRegionId(null);
      setEditingRegionName("");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update region.", variant: "destructive" });
    },
  });

  // Toggle a client type tag on a region (empty array = unrestricted / available to all)
  const toggleRegionClientType = (region: { id: string; clientTypeIds?: string[] | null }, clientTypeId: string) => {
    const current = region.clientTypeIds || [];
    const next = current.includes(clientTypeId)
      ? current.filter(id => id !== clientTypeId)
      : [...current, clientTypeId];
    updateRegionMutation.mutate({ id: region.id, clientTypeIds: next });
  };

  const deleteRegionMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const response = await apiRequest("DELETE", `/api/regions/${id}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw { ...errorData, regionId: id, regionName: name };
      }
      return { id, name };
    },
    onSuccess: (data) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/regions"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      // Remove deleted region from selection if it was selected
      if (selectedRegions.includes(data.id)) {
        setSelectedRegions(prev => prev.filter(id => id !== data.id));
      }
      toast({ title: "Region deleted", description: "Region has been removed." });
    },
    onError: (error: any) => {
      if (error?.hasAssociatedRecords) {
        setRegionDeleteError({
          regionId: error.regionId,
          regionName: error.regionName,
          message: error.message,
          leadCount: error.leadCount || 0,
          clientCount: error.clientCount || 0,
          invoiceCount: error.invoiceCount || 0,
          leads: error.leads || [],
          clients: error.clients || [],
          invoices: error.invoices || [],
        });
        setReassignTargetRegionId("");
      } else {
        toast({ title: "Error", description: error?.message || "Failed to delete region.", variant: "destructive" });
      }
    },
  });

  const deleteRegionWithReassignmentMutation = useMutation({
    mutationFn: async ({ regionId, reassignToRegionId }: { regionId: string; reassignToRegionId: string }) => {
      const response = await apiRequest("POST", `/api/regions/${regionId}/delete-with-reassignment`, { reassignToRegionId });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete region with reassignment");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/regions"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      // Remove deleted region from selection if it was selected
      if (regionDeleteError?.regionId && selectedRegions.includes(regionDeleteError.regionId)) {
        setSelectedRegions(prev => prev.filter(id => id !== regionDeleteError?.regionId));
      }
      setRegionDeleteError(null);
      setReassignTargetRegionId("");
      toast({ title: "Region deleted", description: data.message || "Region deleted and records reassigned." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to delete region with reassignment.", variant: "destructive" });
    },
  });

  const mergeRegionsMutation = useMutation({
    mutationFn: async ({ sourceRegionIds, targetRegionId }: { sourceRegionIds: string[]; targetRegionId: string }) => {
      const res = await apiRequest("POST", "/api/regions/merge", { sourceRegionIds, targetRegionId });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to merge regions");
      return data;
    },
    onSuccess: (data) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/regions"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      setSelectedMergeIds(new Set());
      setIsMergeConfirmOpen(false);
      setMergeTargetId("");
      toast({ title: "Regions merged", description: data.message });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to merge regions.", variant: "destructive" });
    },
  });

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Lead> }) => {
      const response = await apiRequest("PATCH", `/api/leads/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update lead.", variant: "destructive" });
    },
  });

  const updateLeadStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const response = await apiRequest("PATCH", `/api/leads/${id}/status`, { status });
      return response.json();
    },
    onSuccess: (data, { status }) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      setSelectedRowId(null);
      if (status === "booked") {
        toast({ title: "Show Booked", description: "Lead has been moved to clients." });
        const clientId = data?.client?.id;
        const regionId = data?.client?.regionId;
        const params = new URLSearchParams();
        if (clientId) params.set("openClient", clientId);
        if (regionId) params.set("region", regionId);
        navigate(params.toString() ? `/clients?${params.toString()}` : "/clients");
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update status.", variant: "destructive" });
    },
  });

  const createLeadMutation = useMutation({
    mutationFn: async (payload: {
      regionId: string;
      clientType: string;
      leadName?: string;
      contactName?: string;
      phoneNumber?: string;
      mobilePhone?: string;
      emailAddress?: string;
    }) => {
      // Task #324 — auto-tag new leads with the resolved talent (workspace
      // default, or the lone talent in single-talent workspaces) so calendar
      // pushes land on the right talent. Leads stay optional, so we only add
      // talentId when a concrete talent is selected.
      const tagTalentId =
        selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
          ? selectedTalentId
          : undefined;
      const response = await apiRequest("POST", "/api/leads", {
        ...payload,
        createdBy: userId,
        ...(tagTalentId ? { talentId: tagTalentId } : {}),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/leads/${id}`);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      setSelectedRowId(null);
      toast({ title: "Lead deleted", description: "The lead has been removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete lead.", variant: "destructive" });
    },
  });

  const sendEmailViaPreflight = useSendEmailWithPreflight();
  const [sendingEmail, setSendingEmail] = useState(false);

  const sendEmailMutation = {
    isPending: sendingEmail,
    mutate: async ({ to, subject, body, leadId, cc, attachments }: { to: string; subject: string; body: string; leadId?: string; cc?: string; attachments?: StagedAttachment[] }) => {
      // Drop the dialog immediately so the user can keep working while
      // preflight + send (or draft-hold) happen in the background.
      setIsEmailDialogOpen(false);
      const recipient = to;
      const sub = subject;
      setEmailTo("");
      setEmailCc("");
      setEmailSubject("");
      setEmailBody("");
      setEmailAttachments([]);
      setSendingEmail(true);
      const attachmentsPayload = attachments && attachments.length > 0
        ? attachments.map((a) => ({ filename: a.filename, contentBase64: a.contentBase64, contentType: a.contentType }))
        : undefined;
      await sendEmailViaPreflight(
        {
          endpoint: "/api/email/send",
          payload: { to, subject, body, leadId, cc: cc || undefined, attachments: attachmentsPayload },
          recipientEmail: recipient,
          subject: sub,
          description: "lead email",
        },
        {
          onSent: () => {
            refetchActivities();
            toast({ title: "Email sent", description: "Your email has been sent successfully." });
          },
          onQueued: () => {
            // Toast handled by hook; nothing else to do here.
          },
          onError: (error: any) => {
            const { title, description } = describeSendError(error, "Failed to send email.");
            toast({ title, description, variant: "destructive" });
          },
        },
      );
      setSendingEmail(false);
    },
  } as const;

  // Lead activities query and mutation
  const { data: leadActivities = [], refetch: refetchActivities } = useQuery<any[]>({
    queryKey: ["/api/leads", selectedRowId, "activities"],
    queryFn: async () => {
      if (!selectedRowId) return [];
      const response = await fetch(`/api/leads/${selectedRowId}/activities`, { credentials: "include" });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!selectedRowId && (isViewDetailsDialogOpen || isMobileEditOpen),
  });

  const createActivityMutation = useMutation({
    mutationFn: async ({ leadId, type, notes }: { leadId: string; type: string; notes: string }) => {
      return apiRequest("POST", `/api/leads/${leadId}/activities`, { type, notes });
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/gamification/snapshot"] });
      refetchActivities();
      setIsLogCallDialogOpen(false);
      toast({ title: "Call Logged", description: "Your call notes have been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to log call.", variant: "destructive" });
    },
  });

  const createReminderMutation = useMutation({
    mutationFn: async (data: { leadId: string; dueDate: string; note: string }) => {
      const response = await apiRequest("POST", "/api/followup-reminders", data);
      return response.json();
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/followup-reminders"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      setIsSetReminderDialogOpen(false);
      setReminderDate("");
      setReminderNote("");
      toast({ title: "Reminder Set", description: "Your reminder has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set reminder.", variant: "destructive" });
    },
  });

  const bulkEnrichMutation = useMutation({
    mutationFn: async ({ leadIds }: { leadIds: string[] }) => {
      const response = await apiRequest("POST", "/api/leads/bulk-enrich", { leadIds });
      return response.json() as Promise<{ processed: number; enriched: number; creditsCharged: number; outOfCredits: boolean; leads: any[] }>;
    },
    onSuccess: (data) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      setIsEnrichOpen(false);
      toast({
        title: `Enriched ${data.enriched} of ${data.processed} lead${data.processed !== 1 ? "s" : ""}`,
        description: `${data.creditsCharged} credit${data.creditsCharged !== 1 ? "s" : ""} charged.${data.outOfCredits ? " Ran out of credits — remaining leads were skipped." : ""}`,
      });
    },
    onError: (error: any) => {
      const msg = error?.message || "Enrich failed";
      if (msg.includes("402") || error?.status === 402) {
        toast({ title: "Out of credits", description: "Purchase more lead credits to continue enriching.", variant: "destructive" });
      } else {
        toast({ title: "Enrich failed", description: msg, variant: "destructive" });
      }
    },
  });

  const bulkEditMutation = useMutation({
    // ONE request for the whole selection. This used to fan out a PATCH per
    // selected lead; with a few hundred rows that saturated the server's DB
    // pool, made every request (including other users') crawl, and could
    // half-apply the edit before erroring out.
    mutationFn: async ({ ids, field, value }: { ids: string[]; field: string; value: any }) => {
      const response = await apiRequest("POST", "/api/leads/bulk-update", { ids, field, value });
      return response.json() as Promise<{ count: number; requested: number }>;
    },
    onSuccess: (result) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      setSelectedLeadIds(new Set());
      setIsBulkEditOpen(false);
      setBulkEditField("");
      setBulkEditValue("");
      // The server only writes rows the user is actually allowed to see, so
      // the applied count can legitimately be lower than the selection.
      const skipped = result.requested - result.count;
      toast({
        title: "Leads Updated",
        description:
          skipped > 0
            ? `Updated ${result.count} of ${result.requested} leads. ${skipped} were skipped (no longer visible to you or already removed).`
            : `Successfully updated ${result.count} leads.`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update leads.", variant: "destructive" });
    },
  });

  const handleBulkEdit = () => {
    if (selectedLeadIds.size === 0 || !bulkEditField) return;
    bulkEditMutation.mutate({
      ids: Array.from(selectedLeadIds),
      field: bulkEditField,
      value: bulkEditValue,
    });
  };

  // One-shot: parse the `address` field on selected (or all) leads to populate empty city/state/zip.
  // Never overwrites values that are already set.
  const bulkFillFromAddressMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await apiRequest("POST", "/api/leads/bulk-fill-from-address", { ids });
      return response.json() as Promise<{ scanned: number; updated: number; skippedNoAddress: number; skippedAlreadyFilled: number; skippedUnparseable: number }>;
    },
    onSuccess: (result) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      setSelectedLeadIds(new Set());
      setIsBulkEditOpen(false);
      toast({
        title: "Address Parse Complete",
        description: `Updated ${result.updated} of ${result.scanned} leads. Skipped: ${result.skippedAlreadyFilled} already filled, ${result.skippedNoAddress} no address, ${result.skippedUnparseable} unparseable.`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to auto-fill from address.", variant: "destructive" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      // Single server call. If "select all matching" is active we send the
      // current filter set, otherwise we send the explicit checked ids.
      // The filter set MUST mirror what the visible /api/leads query
      // applies — including any active smart-search spec — or the
      // delete count will diverge from the row count the user sees.
      // Manual toolbar selections take precedence over the spec for the
      // same key, mirroring the GET-request merge logic above.
      const manualClientType =
        selectedClientType && selectedClientType !== "__unset__"
          ? selectedClientType
          : null;
      const manualTalentId =
        selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
          ? selectedTalentId
          : null;
      const manualRegionIds =
        selectedRegions.length > 0 ? selectedRegions : null;
      const manualQuadrants =
        quadrantFilter && quadrantFilter.size > 0
          ? Array.from(quadrantFilter).filter((q): q is "NE" | "NW" | "SE" | "SW" =>
              q === "NE" || q === "NW" || q === "SE" || q === "SW",
            )
          : null;
      const body = selectAllMatching
        ? {
            action: "delete" as const,
            scope: {
              type: "filters" as const,
              filters: {
                q: debouncedSearch.trim() || null,
                clientType:
                  manualClientType ?? smartFilters?.clientType ?? null,
                regionIds:
                  manualRegionIds ??
                  (smartFilters?.regionIds && smartFilters.regionIds.length > 0
                    ? smartFilters.regionIds
                    : null),
                talentId: manualTalentId ?? smartFilters?.talentId ?? null,
                quadrants:
                  manualQuadrants ??
                  (smartFilters?.quadrants && smartFilters.quadrants.length > 0
                    ? smartFilters.quadrants
                    : null),
                status: smartFilters?.status ?? null,
                lastContactedDaysAgo:
                  typeof smartFilters?.lastContactedDaysAgo === "number"
                    ? smartFilters.lastContactedDaysAgo
                    : null,
                venueType: smartFilters?.venueType ?? null,
              },
            },
          }
        : {
            action: "delete" as const,
            scope: { type: "ids" as const, ids: Array.from(selectedLeadIds) },
          };
      // Capture the explicit IDs (when not selecting all matching) so the
      // delete can be pushed onto the undo stack and reversed via the
      // per-record restore endpoint.
      const deletedIds = selectAllMatching ? [] : Array.from(selectedLeadIds);
      const response = await apiRequest("POST", "/api/leads/bulk-action", body);
      const json = (await response.json()) as { count: number };
      return { ...json, deletedIds };
    },
    onSuccess: (result) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      // Bulk delete is a soft delete; push it onto the undo stack so it can be
      // reversed. We can only restore by explicit IDs, so "select all matching"
      // deletes (no captured IDs) are not undoable.
      if (result.deletedIds.length > 0) {
        setUndoStack(prev => [...prev, { type: "bulkDelete", leadIds: result.deletedIds }]);
        setRedoStack([]);
      }
      setSelectedLeadIds(new Set());
      setSelectAllMatching(false);
      setIsBulkDeleteOpen(false);
      toast({ title: "Leads Deleted", description: `Successfully deleted ${result.count} leads. Use Undo to restore.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete leads.", variant: "destructive" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (updates: { id: string; sortOrder: number }[]) => {
      return apiRequest("PATCH", "/api/leads/reorder", { updates });
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reorder leads.", variant: "destructive" });
    },
  });

  const handleBulkDelete = () => {
    if (selectedLeadIds.size === 0) return;
    bulkDeleteMutation.mutate();
  };

  // Clear "select all matching" whenever the underlying filters change so a
  // user can't accidentally bulk-delete a different filter set than they
  // visually saw when they ticked the box. The smart-search spec is
  // included here for the same reason — a re-parsed sentence changes the
  // visible row set, so the prior "select all matching" intent is stale.
  useEffect(() => {
    setSelectAllMatching(false);
  }, [
    debouncedSearch,
    selectedClientType,
    selectedTalentId,
    selectedRegions,
    quadrantFilter,
    smartFilters,
  ]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLeadIds(new Set(sortedLeads.filter(l => !l.id.startsWith("empty-")).map(l => l.id)));
    } else {
      setSelectedLeadIds(new Set());
    }
  };

  // Status group for containment — leads cannot be dragged across groups
  const getStatusGroup = (status: string | null | undefined): number => {
    if (status === "interested") return 0;
    if (status === "not_now") return 2;
    if (status === "disqualified") return 3;
    return 1; // active / default
  };

  // Non-admin: drag-and-drop reorder within the same status group
  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    setDraggedLeadId(leadId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, lead: any) => {
    if (lead.id.startsWith("empty-")) return;
    const dragged = sortedLeads.find(l => l.id === draggedLeadId);
    if (!dragged || draggedLeadId === lead.id) return;
    if (getStatusGroup(dragged.status) !== getStatusGroup(lead.status)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverLeadId(lead.id);
  };

  const handleDragLeave = () => {
    setDragOverLeadId(null);
  };

  const handleDrop = (e: React.DragEvent, targetLead: any) => {
    e.preventDefault();
    if (!draggedLeadId || draggedLeadId === targetLead.id || targetLead.id.startsWith("empty-")) {
      setDraggedLeadId(null);
      setDragOverLeadId(null);
      return;
    }
    const dragged = sortedLeads.find(l => l.id === draggedLeadId);
    if (!dragged || getStatusGroup(dragged.status) !== getStatusGroup(targetLead.status)) {
      setDraggedLeadId(null);
      setDragOverLeadId(null);
      return;
    }
    const group = getStatusGroup(dragged.status);
    const groupLeads = sortedLeads.filter(l => !l.id.startsWith("empty-") && getStatusGroup(l.status) === group);
    const fromIdx = groupLeads.findIndex(l => l.id === draggedLeadId);
    const toIdx = groupLeads.findIndex(l => l.id === targetLead.id);
    if (fromIdx === toIdx) { setDraggedLeadId(null); setDragOverLeadId(null); return; }
    const newOrder = [...groupLeads];
    const [removed] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, removed);
    reorderMutation.mutate(newOrder.map((l, i) => ({ id: l.id, sortOrder: i })));
    setDraggedLeadId(null);
    setDragOverLeadId(null);
  };

  const handleDragEnd = () => {
    setDraggedLeadId(null);
    setDragOverLeadId(null);
  };

  // Admin: move selected lead up or down within its status group
  const handleMoveUp = () => {
    if (!selectedRowId || sortField) return;
    const lead = sortedLeads.find(l => l.id === selectedRowId);
    if (!lead) return;
    const group = getStatusGroup(lead.status);
    const groupLeads = sortedLeads.filter(l => !l.id.startsWith("empty-") && getStatusGroup(l.status) === group);
    const idx = groupLeads.findIndex(l => l.id === selectedRowId);
    if (idx <= 0) return;
    const newOrder = [...groupLeads];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    reorderMutation.mutate(newOrder.map((l, i) => ({ id: l.id, sortOrder: i })));
  };

  const handleMoveDown = () => {
    if (!selectedRowId || sortField) return;
    const lead = sortedLeads.find(l => l.id === selectedRowId);
    if (!lead) return;
    const group = getStatusGroup(lead.status);
    const groupLeads = sortedLeads.filter(l => !l.id.startsWith("empty-") && getStatusGroup(l.status) === group);
    const idx = groupLeads.findIndex(l => l.id === selectedRowId);
    if (idx < 0 || idx >= groupLeads.length - 1) return;
    const newOrder = [...groupLeads];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    reorderMutation.mutate(newOrder.map((l, i) => ({ id: l.id, sortOrder: i })));
  };

  const toggleSort = (field: string) => {
    // Server-side sort is the source of truth — silently ignore clicks on
    // columns the storage layer can't sort by. Drag-reorder will keep
    // working in the default ordering for those columns.
    if (!SERVER_SORTABLE.has(field)) return;
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // Reset to default sorting
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    // Server-side sort is the source of truth. Columns the storage layer
    // can't sort by (anything outside SERVER_SORTABLE) get no sort
    // affordance at all — otherwise the user clicks them and nothing
    // visibly happens, which feels broken.
    if (!SERVER_SORTABLE.has(field)) {
      return null;
    }
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    }
    return sortDirection === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const handleSelectLead = (leadId: string, checked: boolean) => {
    const newSet = new Set(selectedLeadIds);
    if (checked) {
      newSet.add(leadId);
    } else {
      newSet.delete(leadId);
    }
    setSelectedLeadIds(newSet);
  };

  const selectedLead = selectedRowId ? leads.find(l => l.id === selectedRowId) : null;

  const handleOpenEmailDialog = () => {
    if (selectedLead) {
      setEmailTo(selectedLead.emailAddress || "");
      setEmailSubject("");
      setEmailBody("");
      setSelectedTemplateId("");
      setIsEmailDialogOpen(true);
    }
  };

  const emailBodyEditorRef = useRef<RichTextEditorHandle>(null);

  const handleInsertEmailVariable = (variable: string) => {
    if (emailBodyEditorRef.current) {
      emailBodyEditorRef.current.insertAtCursor(variable);
    } else {
      setEmailBody((prev) => prev + variable);
    }
  };

  // Query for current user's name (for sender_name merge variable)
  const { data: currentUser } = useQuery<{ firstName?: string; lastName?: string; email?: string }>({
    queryKey: ["/api/me"],
  });

  // Pull effective permissions to gate the qualification override editor.
  const { data: effectivePermissions } = useQuery<{ permissions: Record<string, boolean> }>({
    queryKey: ["/api/permissions/effective"],
    staleTime: 60_000,
  });
  const canOverrideQualification = !!effectivePermissions?.permissions?.["leads.qualification.override"];
  
  // Process merge variables for lead emails
  const processMergeVariables = (text: string, lead: Lead): string => {
    const senderName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}` 
      : currentUser?.firstName || currentUser?.lastName || "";
    
    const replacements: Record<string, string> = {
      "{{contact_name}}": lead.contactName || lead.leadName || "",
      "{{account_name}}": lead.leadName || "",
      "{{client_name}}": lead.leadName || "",
      "{{sender_name}}": senderName,
      "{{date}}": "", // Leads don't have a target date field
      "{{location}}": "", // Leads don't have city/state fields
      "{{phone}}": lead.phoneNumber || "",
      "{{email}}": lead.emailAddress || "",
    };
    
    let result = text;
    for (const [token, value] of Object.entries(replacements)) {
      result = result.replace(new RegExp(token.replace(/[{}]/g, "\\$&"), "g"), value);
    }
    return result;
  };

  const handleSendEmail = () => {
    if (emailTo && emailSubject && emailBody) {
      // Process merge variables before sending
      const processedSubject = processMergeVariables(emailSubject, selectedLead);
      const processedBody = processMergeVariables(emailBody, selectedLead);
      
      sendEmailMutation.mutate({
        to: emailTo,
        subject: processedSubject,
        body: processedBody,
        leadId: selectedRowId || undefined,
        cc: emailCc.trim() || undefined,
        attachments: emailAttachments,
      });
    }
  };

  // Server is now authoritative for sort + filter (see leadsQueryKey above).
  // We keep the `sortedLeads` / `visibleLeads` names for compatibility with
  // the rest of this component but they're now just the flat result of the
  // infinite-query pages.
  const sortedLeads = leads;
  const visibleLeads = leads;
  const hasMoreLeads = !!hasNextPage;

  const emptyRows = useMemo(() => {
    // Don't show placeholder "ghost" rows while more data is still streaming
    // in from the server — they'd shove themselves into the middle of the
    // virtualizer and confuse the row counter.
    if (hasNextPage) return [];
    const existingCount = sortedLeads.length;
    const emptyCount = Math.max(0, visibleRowCount - existingCount);
    return Array.from({ length: emptyCount }, (_, i) => ({
      id: `empty-${i}`,
      isEmpty: true,
    }));
  }, [sortedLeads.length, visibleRowCount, hasNextPage]);

  // IntersectionObserver: ask the infinite query for the next page when the
  // sentinel scrolls into view. Re-attaches whenever the sentinel re-mounts
  // (mobile vs desktop branches share the ref) or when hasNextPage flips.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, sortedLeads.length, isMobile]);

  const handleAddMoreRows = () => {
    setVisibleRowCount(prev => prev + 10);
  };

  const handleCellChange = (leadId: string, column: string, value: string | boolean | number | null) => {
    if (leadId.startsWith("empty-")) {
      if (selectedRegions.length === 0) {
        toast({ 
          title: "Select a Region", 
          description: "Please select a region before adding leads.",
          variant: "destructive" 
        });
        return;
      }
      if (selectedRegions.length > 1) {
        toast({ 
          title: "Select One Region", 
          description: "Please select only one region to add a new lead.",
          variant: "destructive" 
        });
        return;
      }
      createLeadMutation.mutate({ regionId: selectedRegions[0], clientType: selectedClientType === "__unset__" ? "" : selectedClientType });
      return;
    }
    
    // For key identity fields on existing leads, run duplicate checks first
    const isDuplicateField = column === "leadName" || column === "emailAddress" || column === "phoneNumber";
    if (isDuplicateField && typeof value === "string" && value.trim()) {
      const clientMatches = findDuplicateClients(column, value);
      if (clientMatches.length > 0) {
        setMatchedClients(clientMatches);
        setPendingLeadUpdate({ leadId, field: column, value });
        setClientDuplicateOpen(true);
        return;
      }
      // Check duplicate leads (excluding the current lead being edited)
      const leadMatches = findDuplicateLeads(column, value).filter(l => l.id !== leadId);
      if (leadMatches.length > 0) {
        setDuplicateMatchedLeads(leadMatches);
        setSelectedMergeTargetId(leadMatches[0].id);
        setPendingLeadUpdate({ leadId, field: column, value });
        setDuplicateConfirmOpen(true);
        return;
      }
    }

    // Track the old value for undo
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      const oldValue = (lead as any)[column];
      setUndoStack(prev => [...prev, { type: "edit", leadId, field: column, oldValue, newValue: value }]);
      setRedoStack([]);
    }
    
    updateLeadMutation.mutate({ id: leadId, updates: { [column]: value } });
  };

  // Execute a pending lead update (after user confirms past duplicate warning)
  const executePendingLeadUpdate = () => {
    if (!pendingLeadUpdate) return;
    const { leadId, field, value } = pendingLeadUpdate;
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      const oldValue = (lead as any)[field];
      setUndoStack(prev => [...prev, { type: "edit", leadId, field, oldValue, newValue: value }]);
      setRedoStack([]);
    }
    updateLeadMutation.mutate({ id: leadId, updates: { [field]: value } });
    setPendingLeadUpdate(null);
  };

  const handleDeleteWithUndo = (leadId: string) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setUndoStack(prev => [...prev, { type: "delete", lead }]);
      setRedoStack([]);
    }
    deleteLeadMutation.mutate(leadId);
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    
    const action = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    
    if (action.type === "edit") {
      await apiRequest("PATCH", `/api/leads/${action.leadId}`, { [action.field]: action.oldValue });
      setRedoStack(prev => [...prev, action]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Undone", description: "Edit has been reverted." });
    } else if (action.type === "delete") {
      // Recreate the lead
      const { id, ...leadData } = action.lead;
      const response = await apiRequest("POST", "/api/leads", leadData);
      const newLead = await response.json();
      setRedoStack(prev => [...prev, { ...action, lead: { ...action.lead, id: newLead.id } }]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Undone", description: "Lead has been restored." });
    } else if (action.type === "bulkDelete") {
      // Bulk delete is a soft delete — restore each record by ID.
      await Promise.all(
        action.leadIds.map(leadId => apiRequest("POST", `/api/leads/${leadId}/restore`)),
      );
      setRedoStack(prev => [...prev, action]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Undone", description: `Restored ${action.leadIds.length} leads.` });
    }
  };

  const handleRedo = async () => {
    if (redoStack.length === 0) return;
    
    const action = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    
    if (action.type === "edit") {
      await apiRequest("PATCH", `/api/leads/${action.leadId}`, { [action.field]: action.newValue });
      setUndoStack(prev => [...prev, action]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Redone", description: "Edit has been reapplied." });
    } else if (action.type === "delete") {
      await apiRequest("DELETE", `/api/leads/${action.lead.id}`);
      setUndoStack(prev => [...prev, action]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Redone", description: "Lead has been deleted again." });
    } else if (action.type === "bulkDelete") {
      // Re-run the bulk soft delete on the same IDs.
      await apiRequest("POST", "/api/leads/bulk-action", {
        action: "delete",
        scope: { type: "ids", ids: action.leadIds },
      });
      setUndoStack(prev => [...prev, action]);
      queryClientHook.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Redone", description: `Deleted ${action.leadIds.length} leads again.` });
    }
  };

  const getRowClassName = (lead: Lead | { id: string; isEmpty: boolean }) => {
    if ("isEmpty" in lead) return "";
    if (selectedRowId === lead.id) return "spreadsheet-row-selected";
    if (lead.status === "booked") return "spreadsheet-row-booked";
    if (lead.status === "interested") return "spreadsheet-row-interested";
    if (lead.status === "not_now") return "spreadsheet-row-not-now";
    if (lead.status === "disqualified") return "spreadsheet-row-disqualified";
    return "";
  };

  // Normalize phone to digits only for comparison
  const normalizePhoneDigits = (p: string) => (p || "").replace(/\D/g, "");

  // Find matching clients by name, email, or phone (for cross-list duplicate warning)
  const findDuplicateClients = (fieldKey: string, value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) return [];
    return allClients.reduce<Array<{ id: string; accountName: string | null; phoneNumber: string | null; emailAddress: string | null; matchedFields: string[] }>>(
      (acc, client) => {
        const matchedFields: string[] = [];
        if (fieldKey === "leadName" && client.accountName?.trim().toLowerCase() === normalizedValue) {
          matchedFields.push("Account Name");
        }
        if (fieldKey === "emailAddress" && client.emailAddress && client.emailAddress.trim().toLowerCase() === normalizedValue) {
          matchedFields.push("Email Address");
        }
        if (fieldKey === "phoneNumber") {
          const digits = normalizePhoneDigits(value);
          if (digits.length >= 7) {
            if (normalizePhoneDigits(client.phoneNumber || "") === digits || normalizePhoneDigits(client.mobilePhone || "") === digits) {
              matchedFields.push("Phone Number");
            }
          }
        }
        if (matchedFields.length > 0) {
          acc.push({ id: client.id, accountName: client.accountName || null, phoneNumber: client.phoneNumber || null, emailAddress: client.emailAddress || null, matchedFields });
        }
        return acc;
      },
      []
    );
  };

  // Find duplicate leads by name, email, or phone
  const findDuplicateLeads = (fieldKey: string, value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) return [];
    return leads
      .filter(l => !l.id.startsWith("empty-") && !(l as any).isEmpty)
      .reduce<Array<{ id: string; leadName: string | null; emailAddress: string | null; phoneNumber: string | null; createdBy: string; matchedFields: string[] }>>(
        (acc, lead) => {
          const matchedFields: string[] = [];
          if (fieldKey === "leadName" && lead.leadName?.trim().toLowerCase() === normalizedValue) {
            matchedFields.push("Account Name");
          }
          if (fieldKey === "emailAddress" && lead.emailAddress && lead.emailAddress.trim().toLowerCase() === normalizedValue) {
            matchedFields.push("Email Address");
          }
          if (fieldKey === "phoneNumber") {
            const digits = normalizePhoneDigits(value);
            if (digits.length >= 7 && normalizePhoneDigits(lead.phoneNumber || "") === digits) {
              matchedFields.push("Phone Number");
            }
          }
          if (matchedFields.length > 0) {
            acc.push({ id: lead.id, leadName: lead.leadName || null, emailAddress: lead.emailAddress || null, phoneNumber: lead.phoneNumber || null, createdBy: lead.createdBy, matchedFields });
          }
          return acc;
        },
        []
      );
  };

  // Handle modal submit — runs duplicate checks then creates or surfaces the duplicate dialog
  const handleAddLeadModalSubmit = (data: AddLeadFormValues) => {
    const checkFields: Array<{ fieldKey: string; value: string }> = [];
    if (data.leadName?.trim()) checkFields.push({ fieldKey: "leadName", value: data.leadName.trim() });
    if (data.emailAddress?.trim()) checkFields.push({ fieldKey: "emailAddress", value: data.emailAddress.trim() });
    if (data.phoneNumber?.trim()) checkFields.push({ fieldKey: "phoneNumber", value: data.phoneNumber.trim() });

    for (const { fieldKey, value } of checkFields) {
      const clientMatches = findDuplicateClients(fieldKey, value);
      if (clientMatches.length > 0) {
        setMatchedClients(clientMatches);
        setPendingModalLeadData(data);
        setClientDuplicateOpen(true);
        setIsAddLeadModalOpen(false);
        return;
      }
      const leadMatches = findDuplicateLeads(fieldKey, value);
      if (leadMatches.length > 0) {
        setDuplicateMatchedLeads(leadMatches);
        setSelectedMergeTargetId(leadMatches[0].id);
        setPendingModalLeadData(data);
        setDuplicateConfirmOpen(true);
        setIsAddLeadModalOpen(false);
        return;
      }
    }

    // No duplicates — create; modal closes on success so loading state is visible
    createLeadMutation.mutate({
      regionId: data.regionId,
      clientType: data.clientType || "",
      leadName: data.leadName || undefined,
      contactName: data.contactName || undefined,
      phoneNumber: data.phoneNumber || undefined,
      mobilePhone: data.mobilePhone || undefined,
      emailAddress: data.emailAddress || undefined,
    }, {
      onSuccess: () => {
        setIsAddLeadModalOpen(false);
      },
    });
  };

  // Handle creating lead after duplicate confirmation (Create Anyway)
  const handleConfirmedLeadCreate = () => {
    // Modal flow — pendingModalLeadData takes priority
    if (pendingModalLeadData) {
      createLeadMutation.mutate({
        regionId: pendingModalLeadData.regionId,
        clientType: pendingModalLeadData.clientType || "",
        leadName: pendingModalLeadData.leadName || undefined,
        contactName: pendingModalLeadData.contactName || undefined,
        phoneNumber: pendingModalLeadData.phoneNumber || undefined,
        mobilePhone: pendingModalLeadData.mobilePhone || undefined,
        emailAddress: pendingModalLeadData.emailAddress || undefined,
      });
      setPendingModalLeadData(null);
      setDuplicateConfirmOpen(false);
      setDuplicateMatchedLeads([]);
      setSelectedMergeTargetId("");
      return;
    }

    if (!pendingLeadCreate) return;
    
    const { regionId, clientType, fieldKey, value } = pendingLeadCreate;
    createLeadMutation.mutate({ regionId, clientType }, {
      onSuccess: (newLead: any) => {
        if (newLead && newLead.id) {
          updateLeadMutation.mutate({ id: newLead.id, updates: { [fieldKey]: value } });
        }
      }
    });
    
    setPendingLeadCreate(null);
    setDuplicateConfirmOpen(false);
    setDuplicateMatchedLeads([]);
    setSelectedMergeTargetId("");
  };

  // Handle merging new data into an existing lead
  const handleMergeLeadCreate = async () => {
    // Modal flow — merge the first key field from modal data into the selected target
    if (pendingModalLeadData && selectedMergeTargetId) {
      const mergeData: Record<string, string> = {};
      if (pendingModalLeadData.leadName?.trim()) mergeData.leadName = pendingModalLeadData.leadName.trim();
      if (pendingModalLeadData.emailAddress?.trim()) mergeData.emailAddress = pendingModalLeadData.emailAddress.trim();
      if (pendingModalLeadData.phoneNumber?.trim()) mergeData.phoneNumber = pendingModalLeadData.phoneNumber.trim();
      if (pendingModalLeadData.contactName?.trim()) mergeData.contactName = pendingModalLeadData.contactName.trim();
      if (pendingModalLeadData.mobilePhone?.trim()) mergeData.mobilePhone = pendingModalLeadData.mobilePhone.trim();
      try {
        await apiRequest("POST", "/api/leads/merge-data", {
          existingLeadId: selectedMergeTargetId,
          newData: mergeData,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
        toast({ title: "Merged", description: "Lead data merged into the existing record." });
      } catch {
        toast({ title: "Merge Failed", description: "Could not merge lead data.", variant: "destructive" });
      }
      setPendingModalLeadData(null);
      setDuplicateConfirmOpen(false);
      setDuplicateMatchedLeads([]);
      setSelectedMergeTargetId("");
      return;
    }

    if (!pendingLeadCreate || !selectedMergeTargetId) return;
    const { fieldKey, value } = pendingLeadCreate;
    try {
      await apiRequest("POST", "/api/leads/merge-data", {
        existingLeadId: selectedMergeTargetId,
        newData: { [fieldKey]: value },
      });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Merged", description: "Lead data merged into the existing record." });
    } catch {
      toast({ title: "Merge Failed", description: "Could not merge lead data.", variant: "destructive" });
    }
    setPendingLeadCreate(null);
    setDuplicateConfirmOpen(false);
    setDuplicateMatchedLeads([]);
    setSelectedMergeTargetId("");
  };

  // Handle creating lead with duplicate check (runs on all 3 key fields)
  const handleLeadCreateWithDuplicateCheck = (regionId: string, clientType: string, fieldKey: string, value: string) => {
    const shouldCheck = fieldKey === "leadName" || fieldKey === "emailAddress" || fieldKey === "phoneNumber";
    if (shouldCheck) {
      // Check clients first — warn if this account may already be a client
      const clientMatches = findDuplicateClients(fieldKey, value);
      if (clientMatches.length > 0) {
        setMatchedClients(clientMatches);
        setPendingLeadCreate({ regionId, clientType, fieldKey, value });
        setClientDuplicateOpen(true);
        return;
      }
      // Then check duplicate leads
      const leadMatches = findDuplicateLeads(fieldKey, value);
      if (leadMatches.length > 0) {
        setDuplicateMatchedLeads(leadMatches);
        setSelectedMergeTargetId(leadMatches[0].id);
        setPendingLeadCreate({ regionId, clientType, fieldKey, value });
        setDuplicateConfirmOpen(true);
        return;
      }
    }
    
    // No duplicate, create directly
    createLeadMutation.mutate({ regionId, clientType }, {
      onSuccess: (newLead: any) => {
        if (newLead && newLead.id) {
          updateLeadMutation.mutate({ id: newLead.id, updates: { [fieldKey]: value } });
        }
      }
    });
  };

  const handleRowClick = (leadId: string) => {
    if (leadId.startsWith("empty-")) return;
    setSelectedRowId(selectedRowId === leadId ? null : leadId);
  };

  // Get editable columns (exclude region type which uses Select)
  const editableColumns = COLUMNS.filter(col => col.type !== "region");

  const handleTabNavigation = (rowId: string, currentColumn: string, shiftKey: boolean) => {
    const currentIndex = editableColumns.findIndex(col => col.key === currentColumn);
    if (currentIndex === -1) return;

    if (shiftKey) {
      // Move to previous column
      if (currentIndex > 0) {
        const prevColumn = editableColumns[currentIndex - 1];
        setEditingCell({ rowId, column: prevColumn.key });
      }
    } else {
      // Move to next column
      if (currentIndex < editableColumns.length - 1) {
        const nextColumn = editableColumns[currentIndex + 1];
        setEditingCell({ rowId, column: nextColumn.key });
      }
    }
  };

  const isLoading = regionsLoading || leadsLoading;

  return (
    <div className="flex flex-col h-full">
      <SavedViewsTabStrip
        entity="leads"
        getCurrentParams={() => ({
          sort: sortField,
          dir: sortDirection,
          regions: selectedRegions,
          clientType: selectedClientType,
          talentId: selectedTalentId,
        })}
        applyParams={(p) => {
          if (typeof p.sort === "string" || p.sort === null) setSortField((p.sort as any) ?? null);
          if (p.dir === "asc" || p.dir === "desc") setSortDirection(p.dir);
          if (Array.isArray(p.regions)) setSelectedRegions(p.regions.filter((x): x is string => typeof x === "string"));
          if (typeof p.clientType === "string") setSelectedClientType(p.clientType);
          if (typeof p.talentId === "string") setSelectedTalentId(p.talentId);
        }}
        activeViewId={activeViewId}
        onActiveViewChange={setActiveViewId}
        rightSlot={savedViewsRightSlot}
      />
      {/* Desktop toolbar — replaced by a Filters sheet on mobile */}
      {!isMobile && (
      <div className="border-b border-border bg-card overflow-x-auto">
        <div className="flex items-center gap-3 p-4 min-w-max">
          <Label className="text-sm font-medium">Region:</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="w-64 justify-between" 
                data-testid="select-region"
              >
                <span className="truncate">
                  {selectedRegions.length === 0 
                    ? "All Regions" 
                    : selectedRegions.length === 1 
                      ? regions.find(r => r.id === selectedRegions[0])?.name || "1 region"
                      : `${selectedRegions.length} regions selected`}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <div className="p-2 border-b">
                <Input
                  placeholder="Search regions (e.g., OH)"
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  className="h-8"
                  data-testid="input-region-search"
                />
              </div>
              <div className="max-h-60 overflow-auto p-2 space-y-1">
                <div 
                  className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer"
                  onClick={() => setSelectedRegions([])}
                >
                  <Checkbox 
                    checked={selectedRegions.length === 0} 
                    onCheckedChange={() => setSelectedRegions([])}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="checkbox-all-regions"
                  />
                  <span className="text-sm font-medium">All Regions</span>
                </div>
                {filteredRegions.length === 0 && regionSearch.trim() ? (
                  <div className="py-2 px-2 text-sm text-muted-foreground text-center">
                    No regions match "{regionSearch}"
                  </div>
                ) : (
                  filteredRegions.map((region) => (
                    <div 
                      key={region.id}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer"
                      onClick={() => {
                        setSelectedRegions(prev => 
                          prev.includes(region.id) 
                            ? prev.filter(id => id !== region.id)
                            : [...prev, region.id]
                        );
                      }}
                    >
                      <Checkbox 
                        checked={selectedRegions.includes(region.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRegions(prev => [...prev, region.id]);
                          } else {
                            setSelectedRegions(prev => prev.filter(id => id !== region.id));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`checkbox-region-${region.id}`}
                      />
                      <span className="text-sm">{region.name}</span>
                    </div>
                  ))
                )}
              </div>
              {selectedRegions.length > 0 && (
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full" 
                    onClick={() => setSelectedRegions([])}
                    data-testid="button-clear-regions"
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          
          {/* Region management buttons */}
          <Dialog open={isRegionDialogOpen} onOpenChange={setIsRegionDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="button-add-region">
                <Plus className="w-4 h-4 mr-1" />
                New Region
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Region</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="regionName">Region Name</Label>
                  <Input
                    id="regionName"
                    value={newRegionName}
                    onChange={(e) => {
                      setNewRegionName(e.target.value);
                      setRegionNameError(null);
                      setRegionNameWarning(null);
                    }}
                    placeholder="e.g., Columbus, OH"
                    data-testid="input-region-name"
                  />
                  <p className="text-xs text-muted-foreground">Format: City, ST (two-letter state code)</p>
                  {regionNameError && (
                    <p className="text-xs text-destructive">{regionNameError}</p>
                  )}
                  {regionNameWarning && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">{regionNameWarning}</p>
                  )}
                </div>
                <Button
                  onClick={() => {
                    const error = validateRegionFormat(newRegionName);
                    if (error) {
                      if (!regionNameWarning) {
                        setRegionNameWarning("Are you sure? Lists can become harder to manage if they aren't bound by city.");
                        return;
                      }
                      setRegionNameWarning(null);
                    }
                    createRegionMutation.mutate(newRegionName.trim());
                  }}
                  disabled={!newRegionName.trim() || createRegionMutation.isPending}
                  className="w-full"
                  data-testid="button-create-region"
                >
                  {createRegionMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {regionNameWarning ? "Create Anyway" : "Create Region"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {isAdmin && (
            <Dialog open={isManageRegionsOpen} onOpenChange={(open) => { setIsManageRegionsOpen(open); if (!open) { setManageRegionSearch(""); setSelectedMergeIds(new Set()); setMergeTargetId(""); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="button-manage-regions">
                  <Settings className="w-4 h-4 mr-1" />
                  Manage Regions
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manage Regions</DialogTitle>
                </DialogHeader>
                {selectedMergeIds.size >= 2 && (
                  <div className="flex items-center justify-between gap-2 px-1 py-1.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                    <span className="text-sm text-blue-700 dark:text-blue-300 font-medium pl-1">{selectedMergeIds.size} regions selected</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setSelectedMergeIds(new Set())}>
                        Clear
                      </Button>
                      <Button size="sm" className="h-7 text-xs" onClick={() => { setMergeTargetId(""); setIsMergeConfirmOpen(true); }} data-testid="button-merge-regions">
                        Merge {selectedMergeIds.size} Regions…
                      </Button>
                    </div>
                  </div>
                )}
                <div className="pb-2">
                  <Input
                    placeholder="Search regions..."
                    value={manageRegionSearch}
                    onChange={(e) => setManageRegionSearch(e.target.value)}
                    className="h-9"
                    data-testid="input-manage-regions-search"
                  />
                </div>
                <div className="space-y-2 py-2 max-h-80 overflow-y-auto">
                  {regions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No regions yet</p>
                  ) : filteredManageRegions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No regions match "{manageRegionSearch}"</p>
                  ) : (
                    filteredManageRegions.map((region) => (
                      <div
                        key={region.id}
                        className="flex flex-col gap-2 p-2 rounded-md border border-border"
                      >
                        <div className="flex items-center justify-between gap-2">
                        <Checkbox
                          checked={selectedMergeIds.has(region.id)}
                          onCheckedChange={(checked) => {
                            setSelectedMergeIds(prev => {
                              const next = new Set(prev);
                              if (checked) next.add(region.id); else next.delete(region.id);
                              return next;
                            });
                          }}
                          data-testid={`checkbox-merge-region-${region.id}`}
                        />
                        {editingRegionId === region.id ? (
                          <>
                            <Input
                              value={editingRegionName}
                              onChange={(e) => setEditingRegionName(e.target.value)}
                              className="h-8 flex-1"
                              autoFocus
                              data-testid={`input-rename-region-${region.id}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateRegionMutation.mutate({ id: region.id, name: editingRegionName })}
                              disabled={!editingRegionName.trim() || updateRegionMutation.isPending}
                              className="text-green-600 hover:text-green-600 hover:bg-green-500/10"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditingRegionId(null); setEditingRegionName(""); }}
                              className="text-muted-foreground"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm flex-1">{region.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditingRegionId(region.id); setEditingRegionName(region.name); }}
                              className="text-muted-foreground hover:text-foreground"
                              data-testid={`button-edit-region-${region.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteRegionMutation.mutate({ id: region.id, name: region.name })}
                              disabled={deleteRegionMutation.isPending}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`button-delete-region-${region.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        </div>
                        {/* Client type tags — empty = available for all client types */}
                        <div className="flex items-center gap-1.5 flex-wrap pl-7">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">For:</span>
                          {(!region.clientTypeIds || region.clientTypeIds.length === 0) && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal" data-testid={`badge-region-all-${region.id}`}>
                              All client types
                            </Badge>
                          )}
                          {clientTypes.map((ct) => {
                            const isOn = (region.clientTypeIds || []).includes(ct.id);
                            const restricted = (region.clientTypeIds || []).length > 0;
                            // Only show toggleable chips when restricted; if unrestricted, show only the "All" badge plus inline ghost chips to opt-in
                            return (
                              <button
                                key={ct.id}
                                type="button"
                                onClick={() => toggleRegionClientType(region, ct.id)}
                                disabled={updateRegionMutation.isPending}
                                className={
                                  isOn
                                    ? "text-[10px] px-1.5 py-0 h-5 rounded border border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                                    : restricted
                                      ? "text-[10px] px-1.5 py-0 h-5 rounded border border-dashed border-muted-foreground/40 text-muted-foreground hover:bg-muted"
                                      : "text-[10px] px-1.5 py-0 h-5 rounded border border-dashed border-muted-foreground/30 text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                                }
                                data-testid={`button-region-clienttype-${region.id}-${ct.id}`}
                                title={isOn ? `Remove ${ct.name}` : `Restrict to ${ct.name}`}
                              >
                                {ct.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Region Delete with Reassignment Dialog */}
          <AlertDialog open={!!regionDeleteError} onOpenChange={(open) => { if (!open) { setRegionDeleteError(null); setReassignTargetRegionId(""); } }}>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Region "{regionDeleteError?.regionName}"</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4">
                    <p>
                      This region has{" "}
                      {[
                        regionDeleteError?.leadCount ? `${regionDeleteError.leadCount} lead(s)` : null,
                        regionDeleteError?.clientCount ? `${regionDeleteError.clientCount} client(s)` : null,
                        regionDeleteError?.invoiceCount ? `${regionDeleteError.invoiceCount} invoice(s)` : null,
                      ].filter(Boolean).join(", ")}{" "}
                      associated with it.
                    </p>
                    <p>Choose a region to reassign these records to before deleting.</p>
                    
                    <div className="mt-4">
                      <Label className="text-sm font-medium">Reassign to Region:</Label>
                      <Select value={reassignTargetRegionId} onValueChange={setReassignTargetRegionId}>
                        <SelectTrigger className="mt-1" data-testid="select-reassign-region">
                          <SelectValue placeholder="Select a region" />
                        </SelectTrigger>
                        <SelectContent>
                          {regions
                            ?.filter((r: any) => r.id !== regionDeleteError?.regionId)
                            .map((r: any) => (
                              <SelectItem key={r.id} value={r.id} data-testid={`select-item-region-${r.id}`}>
                                {r.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-cancel-region-delete">Cancel</AlertDialogCancel>
                <Button
                  variant="destructive"
                  disabled={!reassignTargetRegionId || deleteRegionWithReassignmentMutation.isPending}
                  onClick={() => {
                    if (regionDeleteError?.regionId && reassignTargetRegionId) {
                      deleteRegionWithReassignmentMutation.mutate({
                        regionId: regionDeleteError.regionId,
                        reassignToRegionId: reassignTargetRegionId,
                      });
                    }
                  }}
                  data-testid="button-confirm-delete-region"
                >
                  {deleteRegionWithReassignmentMutation.isPending ? "Deleting..." : "Delete & Reassign"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Merge Regions Dialog */}
          <AlertDialog open={isMergeConfirmOpen} onOpenChange={(open) => { setIsMergeConfirmOpen(open); if (!open) setMergeTargetId(""); }}>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle>Merge {selectedMergeIds.size} Regions</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4">
                    <p>
                      Select which region to keep. All leads, clients, invoices, and schedule entries from the other regions will be moved to the selected region, and the others will be deleted.
                    </p>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-foreground">Regions being merged:</p>
                      <ul className="text-sm text-muted-foreground space-y-0.5 pl-2">
                        {regions.filter(r => selectedMergeIds.has(r.id)).map(r => (
                          <li key={r.id}>• {r.name}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">Keep this region:</label>
                      <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                        <SelectTrigger data-testid="select-merge-target">
                          <SelectValue placeholder="Select region to keep…" />
                        </SelectTrigger>
                        <SelectContent>
                          {regions.filter(r => selectedMergeIds.has(r.id)).map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button
                  disabled={!mergeTargetId || mergeRegionsMutation.isPending}
                  onClick={() => {
                    if (!mergeTargetId) return;
                    const sourceIds = [...selectedMergeIds].filter(id => id !== mergeTargetId);
                    mergeRegionsMutation.mutate({ sourceRegionIds: sourceIds, targetRegionId: mergeTargetId });
                  }}
                  data-testid="button-confirm-merge-regions"
                >
                  {mergeRegionsMutation.isPending ? "Merging…" : "Merge & Delete Others"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Client Type Filter */}
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-border">
            <Label className="text-sm font-medium">Client Type:</Label>
            <Select value={selectedClientType === "" || selectedClientType === "__unset__" ? "__all__" : selectedClientType} onValueChange={(v) => setSelectedClientType(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="select-client-type-filter">
                <SelectValue placeholder="All Client Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Client Types</SelectItem>
                {clientTypeOptions.map((ct) => (
                  <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Talent Filter */}
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
            <Label className="text-sm font-medium">Talent:</Label>
            <Select
              value={selectedTalentId === "__unset__" ? "" : selectedTalentId}
              onValueChange={(value) => {
                talentPrefExplicitRef.current = true;
                setSelectedTalentId(value);
              }}
            >
              <SelectTrigger className="w-48" data-testid="select-talent-filter">
                <SelectValue placeholder="All Talent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Talent</SelectItem>
                {talent.filter(e => e.isActive).map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} {e.isDefault && "(Default)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Undo/Redo buttons */}
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className="h-11 w-11 sm:h-8 sm:w-8"
              title="Undo"
              data-testid="button-undo"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className="h-11 w-11 sm:h-8 sm:w-8"
              title="Redo"
              data-testid="button-redo"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
          </div>
          
        </div>
      </div>
      )}
      
      {/* Row action toolbar — hidden on mobile (per-card action sheet handles these) */}
      {!isMobile && (
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 border-b border-border bg-muted/50">
        {/* Left side: Email and Log Call */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenEmailDialog}
            disabled={!selectedLead?.emailAddress}
            data-testid="button-email-lead"
          >
            <Mail className="w-4 h-4 mr-1" />
            Email
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsLogCallDialogOpen(true)}
            disabled={!selectedRowId || selectedRowId.startsWith("empty-")}
            data-testid="button-log-call"
          >
            <Phone className="w-4 h-4 mr-1" />
            Log Call
          </Button>
        </div>

        {/* Right side: Set Reminder, View Details, Lead Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSetReminderDialogOpen(true)}
            disabled={!selectedRowId || selectedRowId.startsWith("empty-")}
            data-testid="button-set-reminder"
          >
            <Bell className="w-4 h-4 mr-1" />
            Set Reminder
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsViewDetailsDialogOpen(true)}
            disabled={!selectedRowId || selectedRowId.startsWith("empty-")}
            data-testid="button-view-details"
          >
            <Eye className="w-4 h-4 mr-1" />
            Activity
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedRowId || selectedRowId.startsWith("empty-")} data-testid="dropdown-lead-actions">
                Lead Actions
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    if (!selectedRowId) return;
                    const id = selectedRowId;
                    setSelectedRowId(null);
                    updateLeadStatusMutation.mutate({ id, status: "booked" });
                  }}
                  data-testid="action-book-show"
                  onSelect={(e) => {
                    // Prevent the menu from closing when the user clicks the help icon.
                    if ((e.target as HTMLElement)?.closest?.('[data-testid^="tutorial-hint-"]')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <Calendar className="w-4 h-4 mr-2 text-primary" />
                  <span className="flex-1">Booked</span>
                  <TutorialHint id="lead.convert" className="ml-2" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!selectedRowId) return;
                    const id = selectedRowId;
                    setSelectedRowId(null);
                    updateLeadStatusMutation.mutate({ id, status: "interested" });
                  }}
                  data-testid="action-interested"
                >
                  <Star className="w-4 h-4 mr-2 text-yellow-500" />
                  Interested
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!selectedRowId) return;
                    const id = selectedRowId;
                    setSelectedRowId(null);
                    updateLeadStatusMutation.mutate({ id, status: "not_now" });
                  }}
                  data-testid="action-not-now"
                >
                  <Clock className="w-4 h-4 mr-2 text-orange-500" />
                  Not Now
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!selectedRowId) return;
                    const id = selectedRowId;
                    setSelectedRowId(null);
                    updateLeadStatusMutation.mutate({ id, status: "disqualified" });
                  }}
                  className="text-destructive focus:text-destructive"
                  data-testid="action-disqualify"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Disqualify
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!selectedRowId) return;
                    const id = selectedRowId;
                    setSelectedRowId(null);
                    updateLeadStatusMutation.mutate({ id, status: "active" });
                  }}
                  data-testid="action-clear-status"
                >
                  <RotateCcw className="w-4 h-4 mr-2 text-muted-foreground" />
                  Clear
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => selectedRowId && setConfirmDeleteLeadId(selectedRowId)}
                  disabled={deleteLeadMutation.isPending}
                  className="text-destructive focus:text-destructive"
                  data-testid="action-delete-lead"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="sm"
            onClick={() => {
              if (regions.length === 0) {
                toast({ title: "No Region", description: "Please create a region first.", variant: "destructive" });
                return;
              }
              setIsAddLeadModalOpen(true);
            }}
            disabled={createLeadMutation.isPending}
            data-testid="button-add-lead"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Lead
          </Button>
        </div>
      </div>
      )}

      {/* Email Dialog - outside conditional for stability */}
      <Dialog open={isEmailDialogOpen} onOpenChange={(open) => {
        setIsEmailDialogOpen(open);
        if (!open) setEmailAttachments([]);
      }}>
        <DialogContent className="w-full sm:max-w-[600px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Send Email to {selectedLead?.contactName || selectedLead?.leadName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            <div className="space-y-2">
              <Label htmlFor="emailTo">To</Label>
              <Input 
                id="emailTo"
                type="email"
                value={emailTo} 
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="Enter recipient email..."
                data-testid="input-email-to"
              />
            </div>
            <CcEmailInput value={emailCc} onChange={setEmailCc} data-testid="input-email-cc" />
            <div className="space-y-2">
              <Label htmlFor="templateSelect">Template</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={(value) => {
                  setSelectedTemplateId(value);
                  if (value && value !== "none") {
                    const template = emailTemplates.find(t => t.id === value);
                    if (template) {
                      setEmailSubject(template.subject || "");
                      setEmailBody(template.bodyRich || template.body || "");
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="select-email-template">
                  <SelectValue placeholder="Select a template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {/* Task #696: filter picker-hidden templates. */}
                  {emailTemplates.filter((t) => !t.hiddenFromPickers).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailSubject">Subject</Label>
              <Input
                id="emailSubject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter subject..."
                data-testid="input-email-subject"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="emailBody">Message</Label>
                <div className="flex items-center gap-2">
                  <VoiceToTextButton onTranscript={(t) => emailBodyEditorRef.current?.insertAtCursor(t)} />
                  <EmailVariablesSelector onInsert={handleInsertEmailVariable} />
                </div>
              </div>
              <div className="max-h-[300px] overflow-y-auto border rounded-md">
                <RichTextEditor
                  ref={emailBodyEditorRef}
                  value={emailBody}
                  onChange={setEmailBody}
                  placeholder="Write your message..."
                  minHeight="150px"
                />
              </div>
            </div>
            <EmailAttachmentsField
              value={emailAttachments}
              onChange={setEmailAttachments}
              testIdPrefix="lead-email-attachments"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)} data-testid="button-cancel-email">
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsLeadEmailScheduleOpen(true)}
              disabled={!emailTo || !emailSubject || !emailBody || sendEmailMutation.isPending}
              data-testid="button-schedule-lead-email"
            >
              <Clock className="w-4 h-4 mr-2" />
              Schedule send
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={!emailTo || !emailSubject || !emailBody || sendEmailMutation.isPending}
              data-testid="button-send-email"
            >
              {sendEmailMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduledEmailDialog
        open={isLeadEmailScheduleOpen}
        onOpenChange={setIsLeadEmailScheduleOpen}
        sourceType="one_off"
        recipientSummary={emailTo ? `Schedule email to ${emailTo}` : undefined}
        subjectSummary={emailSubject}
        buildPayload={() => {
          if (!emailTo || !emailSubject || !emailBody) return null;
          return {
            to: emailTo,
            cc: emailCc.trim() || undefined,
            subject: emailSubject,
            htmlBody: emailBody,
            attachments: emailAttachments.length > 0
              ? emailAttachments.map((a) => ({
                  filename: a.filename,
                  contentBase64: a.contentBase64,
                  contentType: a.contentType,
                }))
              : undefined,
            leadId: selectedLead?.id,
          };
        }}
        onScheduled={() => setIsEmailDialogOpen(false)}
      />

      {/* Log Call Dialog */}
      <LogCallDialog
        open={isLogCallDialogOpen}
        onOpenChange={setIsLogCallDialogOpen}
        title={`Log Call for ${selectedLead?.contactName || selectedLead?.leadName || "Lead"}`}
        onSave={(notes) => {
          if (selectedRowId) {
            createActivityMutation.mutate({ leadId: selectedRowId, type: "call", notes });
          }
        }}
        isPending={createActivityMutation.isPending}
      />

      {/* Notes Edit Dialog (opens on double-click of notes cell) */}
      <Dialog
        open={editNotesLeadId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditNotesLeadId(null);
            setEditNotesValue("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>
              Notes — {leads.find((l) => l.id === editNotesLeadId)?.leadName || "Lead"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Textarea
              value={editNotesValue}
              onChange={(e) => setEditNotesValue(e.target.value)}
              placeholder="Add notes for this lead…"
              rows={14}
              className="resize-y min-h-[260px] font-mono text-sm"
              data-testid="textarea-edit-notes"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditNotesLeadId(null);
                  setEditNotesValue("");
                }}
                data-testid="button-cancel-edit-notes"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (editNotesLeadId) {
                    handleCellChange(editNotesLeadId, "notes", editNotesValue);
                  }
                  setEditNotesLeadId(null);
                  setEditNotesValue("");
                }}
                data-testid="button-save-edit-notes"
              >
                Save Notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* View Details Dialog */}
      <Dialog open={isViewDetailsDialogOpen} onOpenChange={setIsViewDetailsDialogOpen}>
        <DialogContent className="w-full sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 pr-6">
              <span>Activity Timeline - {selectedLead?.contactName || selectedLead?.leadName}</span>
              {selectedRowId && (
                <CatchMeUpButton
                  entityType="lead"
                  entityId={selectedRowId}
                  eventCount={leadActivities.length}
                />
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedLead && (
              <div className="p-4 bg-muted rounded-lg space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Lead Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Account:</span> {selectedLead.leadName || "N/A"}</div>
                    <div><span className="text-muted-foreground">Contact:</span> {selectedLead.contactName || "N/A"}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {formatPhoneNumber(selectedLead.phoneNumber) || "N/A"}</div>
                    <div><span className="text-muted-foreground">Email:</span> {selectedLead.emailAddress || "N/A"}</div>
                    <div><span className="text-muted-foreground">Status:</span> {selectedLead.status}</div>
                    <div><span className="text-muted-foreground">Notes:</span> {selectedLead.notes || "N/A"}</div>
                  </div>
                </div>
                {/* LeadQualificationCard hidden — parked for future enhancement */}
              </div>
            )}

            {/* Additional contacts — mirrors the client record contacts panel */}
            {selectedRowId && (
              <LeadContactsSection leadId={selectedRowId} />
            )}

            {/* Task #599 — Lead Catcher transcript card. Renders nothing
                when the lead wasn't created via the catcher. */}
            {selectedRowId && (
              <LeadCatcherConversationCard leadId={selectedRowId} />
            )}

            {selectedRowId && (
              <LeadOutboundCallCard
                leadId={selectedRowId}
                leadName={selectedLead?.leadName}
                contactName={selectedLead?.contactName}
              />
            )}

            <div>
              <h4 className="font-medium mb-3">Activity History</h4>
              {leadActivities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity history found.</p>
              ) : (
                <div className="space-y-3">
                  {leadActivities.map((activity: any) => {
                    const isEditing = editingActivityId === activity.id;
                    // Dialer-sourced rows live in the calls table; edit/delete
                    // must go through the Dialer Hub, not the activity PATCH/DELETE.
                    const isDialerEntry = activity.source === "dialer";
                    const isSystemEntry = isDialerEntry;
                    const typeLabel = activity.type;

                    // Human-readable duration for dialer calls
                    const fmtDuration = (secs: number) => {
                      if (!secs) return null;
                      const m = Math.floor(secs / 60);
                      const s = secs % 60;
                      return m > 0 ? `${m}m ${s}s` : `${s}s`;
                    };

                    return (
                      <div key={activity.id} className="flex gap-3 p-3 border rounded-lg group" data-testid={`row-lead-activity-${activity.id}`}>
                        <div className="flex-shrink-0">
                          {activity.type === "call" && <Phone className="w-5 h-5 text-blue-500" />}
                          {activity.type === "email" && <Mail className="w-5 h-5 text-green-500" />}
                          {activity.type === "note" && <Pencil className="w-5 h-5 text-orange-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium capitalize" data-testid={`text-activity-type-${activity.id}`}>
                                {typeLabel}
                              </span>
                              {/* Dialer call meta-badges */}
                              {isDialerEntry && (
                                <>
                                  <Badge
                                    variant="outline"
                                    className="text-xs capitalize"
                                    data-testid={`badge-call-direction-${activity.id}`}
                                  >
                                    {activity.direction}
                                  </Badge>
                                  {activity.durationSeconds > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                      data-testid={`badge-call-duration-${activity.id}`}
                                    >
                                      {fmtDuration(activity.durationSeconds)}
                                    </Badge>
                                  )}
                                  {activity.status && activity.status !== "completed" && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs capitalize text-muted-foreground"
                                      data-testid={`badge-call-status-${activity.id}`}
                                    >
                                      {activity.status}
                                    </Badge>
                                  )}
                                </>
                              )}
                              {activity.type === "email" && activity.opened !== undefined && (
                                activity.opened ? (
                                  <Badge
                                    variant="outline"
                                    className="text-xs border-green-500/40 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
                                    data-testid={`badge-email-opened-${activity.id}`}
                                  >
                                    Opened
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-xs text-muted-foreground"
                                    data-testid={`badge-email-unopened-${activity.id}`}
                                  >
                                    Unopened
                                  </Badge>
                                )
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {new Date(activity.createdAt).toLocaleString()}
                              </span>
                              {!isEditing && !isSystemEntry && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setEditingActivityId(activity.id);
                                      setEditingActivityNotes(activity.notes || "");
                                    }}
                                    data-testid={`button-edit-activity-${activity.id}`}
                                    title="Edit"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive hover:text-destructive"
                                    onClick={async () => {
                                      if (!selectedRowId) return;
                                      if (!confirm("Delete this activity? This cannot be undone.")) return;
                                      try {
                                        await apiRequest("DELETE", `/api/leads/${selectedRowId}/activities/${activity.id}`);
                                        queryClient.invalidateQueries({ queryKey: [`/api/leads/${selectedRowId}/activities`] });
                                        toast({ title: "Activity deleted" });
                                      } catch (e: any) {
                                        toast({ title: "Failed to delete", description: e?.message, variant: "destructive" });
                                      }
                                    }}
                                    data-testid={`button-delete-activity-${activity.id}`}
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          {isEditing ? (
                            <div className="mt-2 space-y-2">
                              <Textarea
                                value={editingActivityNotes}
                                onChange={(e) => setEditingActivityNotes(e.target.value)}
                                rows={4}
                                className="text-sm"
                                data-testid={`textarea-edit-activity-${activity.id}`}
                                autoFocus
                              />
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditingActivityId(null);
                                    setEditingActivityNotes("");
                                  }}
                                  data-testid={`button-cancel-edit-activity-${activity.id}`}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    if (!selectedRowId) return;
                                    try {
                                      await apiRequest("PATCH", `/api/leads/${selectedRowId}/activities/${activity.id}`, {
                                        notes: editingActivityNotes,
                                      });
                                      queryClient.invalidateQueries({ queryKey: [`/api/leads/${selectedRowId}/activities`] });
                                      setEditingActivityId(null);
                                      setEditingActivityNotes("");
                                      toast({ title: "Activity updated" });
                                    } catch (e: any) {
                                      toast({ title: "Failed to save", description: e?.message, variant: "destructive" });
                                    }
                                  }}
                                  data-testid={`button-save-edit-activity-${activity.id}`}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            activity.notes && (
                              <div className="mt-1">
                                <ExpandableText text={activity.notes} className="text-sm text-muted-foreground" />
                              </div>
                            )
                          )}
                          {/* Recording player for dialer calls */}
                          {!isEditing && isDialerEntry && activity.recordingUrl && (
                            <div className="mt-2 flex items-center gap-2" data-testid={`recording-player-activity-${activity.id}`}>
                              <audio
                                controls
                                src={`/api/dialer/calls/${activity.id}/recording`}
                                className="h-8 w-full max-w-xs"
                                data-testid={`audio-activity-recording-${activity.id}`}
                              />
                              <a
                                href={`/api/dialer/calls/${activity.id}/recording`}
                                download
                                className="text-xs text-muted-foreground hover:text-foreground"
                                data-testid={`link-activity-recording-download-${activity.id}`}
                                title="Download recording"
                              >
                                ↓
                              </a>
                            </div>
                          )}
                          {activity.createdByName && !isEditing && (
                            <p className="text-xs text-muted-foreground mt-1">By: {activity.createdByName}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Reminder Dialog */}
      <Dialog open={isSetReminderDialogOpen} onOpenChange={setIsSetReminderDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Set Reminder for {selectedLead?.contactName || selectedLead?.leadName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reminderDate">Due Date</Label>
              <Input
                id="reminderDate"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                data-testid="input-reminder-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reminderNote">Note (optional)</Label>
              <Textarea
                id="reminderNote"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="Add a note for this reminder..."
                rows={3}
                data-testid="input-reminder-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSetReminderDialogOpen(false)}
              data-testid="button-cancel-reminder"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedRowId && reminderDate) {
                  createReminderMutation.mutate({
                    leadId: selectedRowId,
                    dueDate: reminderDate,
                    note: reminderNote,
                  });
                }
              }}
              disabled={!reminderDate || createReminderMutation.isPending}
              data-testid="button-save-reminder"
            >
              {createReminderMutation.isPending ? "Saving..." : "Save Reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Toolbar */}
      {selectedLeadIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/10 border-b border-border flex-wrap">
          <span className="text-sm font-medium" data-testid="text-bulk-selection-count">
            {selectAllMatching
              ? `All leads matching current filters selected`
              : `${selectedLeadIds.size} selected`}
          </span>
          {!selectAllMatching && hasNextPage && (
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => setSelectAllMatching(true)}
              data-testid="button-select-all-matching-leads"
            >
              Select all leads matching current filters
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsCampaignChooserOpen(true)}
            disabled={selectedLeadIds.size === 0}
            data-testid="button-campaign-leads"
          >
            <Megaphone className="w-4 h-4 mr-1" />
            Campaign
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEnrichOpen(true)}
            disabled={selectedLeadIds.size === 0}
            data-testid="button-enrich-leads"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Enrich
          </Button>
          {canBulkActions && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBulkEditOpen(true)}
              data-testid="button-bulk-edit-leads"
            >
              <Edit className="w-4 h-4 mr-1" />
              Bulk Edit
            </Button>
          )}
          {canBulkActions && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsBulkDeleteOpen(true)}
              data-testid="button-bulk-delete-leads"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedLeadIds(new Set());
              setSelectAllMatching(false);
            }}
            data-testid="button-clear-selection"
          >
            Clear Selection
          </Button>
          {isAdmin && !sortField && (
            <div className="flex items-center gap-1 ml-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={handleMoveUp}
                disabled={!selectedRowId || selectedRowId.startsWith("empty-") || reorderMutation.isPending}
                title="Move Up"
                data-testid="button-move-up"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={handleMoveDown}
                disabled={!selectedRowId || selectedRowId.startsWith("empty-") || reorderMutation.isPending}
                title="Move Down"
                data-testid="button-move-down"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedLeadIds.size} Leads?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            The selected leads will be removed from your list. This can be reversed with the Undo button.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsBulkDeleteOpen(false)} data-testid="button-cancel-bulk-delete-leads">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
              data-testid="button-confirm-bulk-delete-leads"
            >
              {bulkDeleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete {selectedLeadIds.size} Leads
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Lead Delete Confirmation */}
      <AlertDialog open={!!confirmDeleteLeadId} onOpenChange={(open) => { if (!open) setConfirmDeleteLeadId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will delete this record as a client, all invoices, and all documents associated with this client, are you sure you want to delete this record?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-lead">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmDeleteLeadId) handleDeleteWithUndo(confirmDeleteLeadId);
                setConfirmDeleteLeadId(null);
              }}
              data-testid="button-confirm-delete-lead"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Enrich Dialog */}
      <Dialog open={isEnrichOpen} onOpenChange={(v) => { if (!bulkEnrichMutation.isPending) setIsEnrichOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Enrich {Math.min(selectedLeadIds.size, 50)} Lead{Math.min(selectedLeadIds.size, 50) !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Search Apollo + email finder for missing phone numbers, email addresses, and contact names.
              {selectedLeadIds.size > 50 && (
                <span className="block mt-1 text-amber-600 dark:text-amber-400 font-medium">
                  Only the first 50 selected leads will be processed per batch.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
              <div className="font-medium">Credit cost</div>
              <div className="text-muted-foreground">
                <span className="font-medium text-foreground">1 lead credit per lead enriched</span> — credits are refunded automatically if nothing is found.
              </div>
              <div className="text-muted-foreground text-xs">Up to {Math.min(selectedLeadIds.size, 50)} credit{Math.min(selectedLeadIds.size, 50) !== 1 ? "s" : ""} may be charged.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEnrichOpen(false)} disabled={bulkEnrichMutation.isPending} data-testid="button-enrich-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => bulkEnrichMutation.mutate({ leadIds: Array.from(selectedLeadIds).slice(0, 50) })}
              disabled={bulkEnrichMutation.isPending || selectedLeadIds.size === 0}
              data-testid="button-enrich-confirm"
            >
              {bulkEnrichMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enriching…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Enrich Now</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedLeadIds.size} Leads</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* One-shot: auto-fill City / State / Zip from the address field */}
            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <div className="text-sm font-medium">Auto-fill from Address</div>
              <p className="text-xs text-muted-foreground">
                Parses the existing <span className="font-mono">Address</span> field and fills any empty City, State, or Zip values. Never overwrites data you already have.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkFillFromAddressMutation.mutate(Array.from(selectedLeadIds))}
                disabled={bulkFillFromAddressMutation.isPending || selectedLeadIds.size === 0}
                data-testid="button-bulk-fill-from-address"
              >
                {bulkFillFromAddressMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Auto-fill {selectedLeadIds.size} Lead{selectedLeadIds.size === 1 ? "" : "s"} from Address
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Field to Update</Label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger data-testid="select-bulk-field">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notes">Notes</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="regionId">Region</SelectItem>
                  <SelectItem value="talentId">Talent</SelectItem>
                  <SelectItem value="city">City</SelectItem>
                  <SelectItem value="state">State</SelectItem>
                  <SelectItem value="zipCode">Zip Code</SelectItem>
                  <SelectItem value="emailAddress">Email Address</SelectItem>
                  {isAdmin && <SelectItem value="rep">Rep</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {bulkEditField === "talentId" && (
              <div className="space-y-2">
                <Label>New Talent</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-talent">
                    <SelectValue placeholder="Select talent" />
                  </SelectTrigger>
                  <SelectContent>
                    {talent.filter(e => e.isActive).map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`option-bulk-talent-${e.id}`}>
                        {e.name} {e.isDefault && "(Default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(bulkEditField === "city" || bulkEditField === "state" || bulkEditField === "zipCode") && (
              <div className="space-y-2">
                <Label>New Value</Label>
                <Input
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder={
                    bulkEditField === "city" ? "e.g., Austin"
                    : bulkEditField === "state" ? "e.g., TX"
                    : "e.g., 78701"
                  }
                  data-testid="input-bulk-address-value"
                />
              </div>
            )}
            {bulkEditField === "notes" && (
              <div className="space-y-2">
                <Label>New Value</Label>
                <Textarea
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="Enter notes..."
                  data-testid="input-bulk-value"
                />
              </div>
            )}
            {bulkEditField === "status" && (
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (Clear)</SelectItem>
                    <SelectItem value="interested">Interested</SelectItem>
                    <SelectItem value="not_now">Not Now</SelectItem>
                    <SelectItem value="disqualified">Disqualified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {bulkEditField === "regionId" && (
              <div className="space-y-2">
                <Label>New Region</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-region">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientTypeFilteredRegions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClientType && selectedClientType !== "__unset__" && (
                  <p className="text-xs text-muted-foreground">
                    Showing regions tagged for "{selectedClientType}".
                  </p>
                )}
              </div>
            )}
            {bulkEditField === "emailAddress" && (
              <div className="space-y-2">
                <Label>New Email Address</Label>
                <Input
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder="e.g., contact@venue.com"
                  data-testid="input-bulk-email-value"
                />
              </div>
            )}
            {bulkEditField === "rep" && (
              <div className="space-y-2">
                <Label>New Rep</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-rep">
                    <SelectValue placeholder="Select rep" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => {
                      const displayName = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
                      return (
                        <SelectItem key={u.id} value={displayName} data-testid={`option-bulk-rep-${u.id}`}>
                          {displayName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={handleBulkEdit}
              disabled={!bulkEditField || !bulkEditValue || bulkEditMutation.isPending}
              className="w-full"
              data-testid="button-apply-bulk-edit"
            >
              {bulkEditMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply to {selectedLeadIds.size} Leads
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task #211 — Campaign action for the selected leads: Mass Email or
          Drip Campaign. Resolves the selected IDs to loaded lead rows so
          the dialogs can show valid/skipped counts and map enroll payloads. */}
      <LeadCampaignDialogs
        open={isCampaignChooserOpen}
        onOpenChange={setIsCampaignChooserOpen}
        selectedLeads={leads.filter((l) => selectedLeadIds.has(l.id))}
        emailTemplates={emailTemplates}
        onCompleted={() => {
          setSelectedLeadIds(new Set());
          setSelectAllMatching(false);
        }}
      />

      {/* Always-visible status bar above the scroll area. Lives outside the
          scrollable region so it stays pinned regardless of scroll position
          or which branch (desktop vs mobile) renders the rows. Two distinct
          notes: drag-disabled-by-sort, and search-scope-limited-by-encryption. */}
      {(sortField || debouncedSearch.trim()) && (
        <div className="flex flex-col border-b border-border bg-muted/20">
          {sortField && (
            <div className="h-6 flex items-center justify-center text-[11px] text-muted-foreground" data-testid="hint-sort-active">
              Sorted by {sortField} — clear sort to drag-reorder
            </div>
          )}
          {debouncedSearch.trim() && (
            <div className="h-6 flex items-center justify-center text-[11px] text-muted-foreground" data-testid="hint-search-scope">
              Searching name, contact, email, city, phone, notes &amp; address
            </div>
          )}
        </div>
      )}

      {/* Spreadsheet or Mobile Card List */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : leadsIsError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground" data-testid="state-leads-error">
            <p className="text-sm font-medium">Failed to load leads. Please try again.</p>
            <button
              className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => refetchLeads()}
              data-testid="error-retry-button"
            >
              Retry
            </button>
          </div>
        ) : isMobile ? (
          <div className="p-3 space-y-2">
            {/* Mobile: Filters (Region / Client Type / Talent) */}
            <MobileFilterSheet
              activeCount={
                (selectedRegions.length > 0 ? 1 : 0) +
                (selectedClientType && selectedClientType !== "__unset__" ? 1 : 0) +
                (selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__" ? 1 : 0)
              }
              chips={[
                ...(selectedRegions.length > 0
                  ? [{
                      key: "regions",
                      label:
                        selectedRegions.length === 1
                          ? regions.find((r) => r.id === selectedRegions[0])?.name || "1 region"
                          : `${selectedRegions.length} regions`,
                      onRemove: () => setSelectedRegions([]),
                    }]
                  : []),
                ...(selectedClientType && selectedClientType !== "__unset__"
                  ? [{ key: "clientType", label: selectedClientType, onRemove: () => setSelectedClientType("") }]
                  : []),
                ...(selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
                  ? [{
                      key: "talent",
                      label: talent.find((t) => t.id === selectedTalentId)?.name || "Talent",
                      onRemove: () => setSelectedTalentId("all"),
                    }]
                  : []),
              ]}
              onClearAll={() => {
                setSelectedRegions([]);
                setSelectedClientType("");
                setSelectedTalentId("all");
              }}
            >
              <MobileFilterField label="Region">
                <MobileRegionPicker
                  regions={regions}
                  selected={selectedRegions}
                  onChange={setSelectedRegions}
                />
              </MobileFilterField>
              <MobileFilterField label="Client Type">
                <Select
                  value={selectedClientType === "" || selectedClientType === "__unset__" ? "__all__" : selectedClientType}
                  onValueChange={(v) => setSelectedClientType(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger className="h-11" data-testid="select-mobile-client-type">
                    <SelectValue placeholder="All Client Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Client Types</SelectItem>
                    {clientTypeOptions.map((ct) => (
                      <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </MobileFilterField>
              <MobileFilterField label="Talent">
                <Select
                  value={selectedTalentId === "__unset__" ? "" : selectedTalentId}
                  onValueChange={(value) => {
                    talentPrefExplicitRef.current = true;
                    setSelectedTalentId(value);
                  }}
                >
                  <SelectTrigger className="h-11" data-testid="select-mobile-talent">
                    <SelectValue placeholder="All Talent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Talent</SelectItem>
                    {talent.filter((e) => e.isActive).map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.isDefault && "(Default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </MobileFilterField>
            </MobileFilterSheet>

            {/* Mobile: Add Lead button */}
            <Button
              className="w-full h-11 justify-center gap-2"
              onClick={() => {
                if (regions.length === 0) {
                  toast({ title: "No Region", description: "Please create a region first.", variant: "destructive" });
                  return;
                }
                setIsAddLeadModalOpen(true);
              }}
              disabled={createLeadMutation.isPending}
              data-testid="button-mobile-add-lead"
            >
              <Plus className="w-4 h-4" />
              Add New Lead
            </Button>

            {visibleLeads.filter(l => !l.id.startsWith("empty-")).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p className="text-sm">No leads found</p>
              </div>
            ) : (
              visibleLeads.filter(l => !l.id.startsWith("empty-")).map((lead) => {
                const region = regions.find(r => r.id === lead.regionId);
                const statusDot: Record<string, string> = {
                  booked: "bg-primary",
                  interested: "bg-blue-500",
                  not_now: "bg-amber-500",
                  disqualified: "bg-destructive",
                };
                const statusLabel: Record<string, string> = {
                  interested: "Interested",
                  not_now: "Not Now",
                  disqualified: "Disqualified",
                  booked: "Booked",
                  active: "Active",
                };
                const name = lead.leadName || "Unnamed Lead";
                return (
                  <div
                    key={lead.id}
                    className="rounded-2xl border border-border bg-card p-4 active:scale-[0.99] active:bg-foreground/[0.02] transition-transform cursor-pointer"
                    onClick={() => { setSelectedRowId(lead.id); setIsMobileActionSheetOpen(true); }}
                    data-testid={`mobile-lead-card-${lead.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ${bsAvatarSwatch(name)}`}>
                        {bsInitials(name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-semibold leading-tight text-foreground">{name}</p>
                        {lead.contactName && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">{lead.contactName}</p>
                        )}
                      </div>
                      {lead.status && lead.status !== "active" && (
                        <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                          <span className={`h-1.5 w-1.5 rounded-full ${statusDot[lead.status] || "bg-muted-foreground"}`} />
                          {statusLabel[lead.status] || lead.status}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                      {lead.phoneNumber && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                          <a href={`tel:${lead.phoneNumber}`} className="text-foreground/90" onClick={e => e.stopPropagation()}>{formatPhoneNumber(lead.phoneNumber)}</a>
                        </div>
                      )}
                      {lead.emailAddress && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                          <span className="truncate">{lead.emailAddress}</span>
                          {invalidEmailSet.has((lead.emailAddress || "").toLowerCase()) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`badge-invalid-email-${lead.id}`}
                                  className="inline-block w-2 h-2 rounded-full bg-destructive shrink-0 cursor-default"
                                />
                              </TooltipTrigger>
                              <TooltipContent>Invalid Email — NeverBounce</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      )}
                      {(region || lead.lastContacted) && (
                        <div className="flex items-center gap-3 pt-0.5 text-[11px] text-muted-foreground/70">
                          {region && (
                            <span className="flex items-center gap-1.5">
                              <Building2 className="h-3 w-3 shrink-0" />
                              {region.name}
                            </span>
                          )}
                          {lead.lastContacted && (
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-3 w-3 shrink-0" />
                              {formatLocalDate(lead.lastContacted, { month: "short", day: "numeric" })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {lead.notes && (
                      <p className="mt-3 border-t border-border/60 pt-2.5 text-xs text-muted-foreground/80 line-clamp-2">{lead.notes}</p>
                    )}
                  </div>
                );
              })
            )}
            {hasMoreLeads && (
              <div ref={loadMoreSentinelRef} className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                Loading more…
              </div>
            )}
          </div>
        ) : (
          <div className="min-w-max">
            {/* Header */}
            <div className="flex sticky top-0 bg-muted border-b border-border z-10">
              <div className="w-8 shrink-0 border-r border-border flex items-center justify-center">
                <Checkbox
                  checked={sortedLeads.filter(l => !l.id.startsWith("empty-")).length > 0 && 
                    sortedLeads.filter(l => !l.id.startsWith("empty-")).every(l => selectedLeadIds.has(l.id))}
                  onCheckedChange={handleSelectAll}
                  data-testid="checkbox-select-all-leads"
                />
              </div>
              {COLUMNS.map((col) => (
                <div
                  key={col.key}
                  draggable
                  onDragStart={(e) => {
                    setDraggedColKey(col.key);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", col.key); } catch {}
                  }}
                  onDragOver={(e) => {
                    if (!draggedColKey || draggedColKey === col.key) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverColKey !== col.key) setDragOverColKey(col.key);
                  }}
                  onDragLeave={() => {
                    if (dragOverColKey === col.key) setDragOverColKey(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = draggedColKey || e.dataTransfer.getData("text/plain");
                    if (src && src !== col.key) moveColumn(src, col.key);
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                  }}
                  onDragEnd={() => {
                    setDraggedColKey(null);
                    setDragOverColKey(null);
                  }}
                  className={cn(
                    "spreadsheet-cell font-medium text-sm bg-muted relative group cursor-grab active:cursor-grabbing",
                    draggedColKey === col.key && "opacity-40",
                    dragOverColKey === col.key && draggedColKey !== col.key && "border-l-2 border-l-primary"
                  )}
                  style={{ width: getColumnWidth(col.key), minWidth: 50 }}
                  data-testid={`column-header-${col.key}`}
                >
                  <button
                    onClick={() => toggleSort(col.key)}
                    className="flex items-center hover:text-foreground w-full"
                    data-testid={`sort-${col.key}`}
                  >
                    {col.label}
                    <SortIcon field={col.key} />
                  </button>
                  <div
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50 group-hover:bg-border"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </div>
              ))}
            </div>

            {/* Rows — virtualized via @tanstack/react-virtual. Only the
                rows in (or near) the viewport actually render to the DOM,
                so scrolling stays smooth even with thousands of loaded
                leads. The outer absolutely-positioned wrapper holds the
                full virtual height so the scroll bar reflects the real
                row count. Pages stream in via fetchNextPage as the user
                approaches the bottom. */}
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const lead = visibleLeads[virtualRow.index];
              const index = virtualRow.index;
              if (!lead) return null;
              return (
              <div
                key={lead.id}
                data-index={virtualRow.index}
                className={cn(
                  "flex border-b border-border hover:bg-muted/30 transition-colors",
                  getRowClassName(lead),
                  selectedLeadIds.has(lead.id) && "bg-primary/10",
                  !isAdmin && dragOverLeadId === lead.id && draggedLeadId !== lead.id && "border-t-2 border-t-primary"
                )}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                onDragOver={!isAdmin && !lead.id.startsWith("empty-") && !sortField ? (e) => handleDragOver(e, lead) : undefined}
                onDrop={!isAdmin && !lead.id.startsWith("empty-") && !sortField ? (e) => handleDrop(e, lead) : undefined}
              >
                  <div
                  className={cn(
                    "w-8 shrink-0 h-7 flex items-center justify-center text-xs text-muted-foreground border-r border-border",
                    !isAdmin && !lead.id.startsWith("empty-") && !sortField
                      ? "bg-muted/30 cursor-grab active:cursor-grabbing hover:bg-primary/10"
                      : "bg-muted/50",
                    !isAdmin && draggedLeadId === lead.id && "opacity-40"
                  )}
                  onClick={() => isAdmin && !lead.id.startsWith("empty-") && handleRowClick(lead.id)}
                  draggable={!isAdmin && !lead.id.startsWith("empty-") && !sortField}
                  onDragStart={!isAdmin && !lead.id.startsWith("empty-") && !sortField ? (e) => handleDragStart(e, lead.id) : undefined}
                  onDragEnd={!isAdmin ? handleDragEnd : undefined}
                  data-testid={`drag-handle-${lead.id}`}
                >
                  {!lead.id.startsWith("empty-") ? (
                    <Checkbox
                      checked={selectedLeadIds.has(lead.id)}
                      onCheckedChange={(checked) => handleSelectLead(lead.id, !!checked)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`checkbox-lead-${lead.id}`}
                    />
                  ) : (
                    index + 1
                  )}
                </div>
                {COLUMNS.map((col) => (
                  <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}>
                    {col.type === "region" ? (
                      (() => {
                        const allowed = regionsForClientType(lead.clientType);
                        // Always include the currently-selected region even if no longer allowed (avoid orphan)
                        const optionRegions = lead.regionId && !allowed.find(r => r.id === lead.regionId)
                          ? [...allowed, ...regions.filter(r => r.id === lead.regionId)]
                          : allowed;
                        return (
                          <Select
                            value={lead.regionId}
                            onValueChange={(value) => handleCellChange(lead.id, "regionId", value)}
                          >
                            <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {optionRegions.map((region) => (
                                <SelectItem key={region.id} value={region.id}>
                                  {region.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      })()
                    ) : col.type === "talent" ? (
                      <Select
                        value={(lead as any).talentId || ""}
                        onValueChange={(value) => handleCellChange(lead.id, "talentId", value)}
                      >
                        <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0" data-testid={`select-talent-${lead.id}`}>
                          <SelectValue placeholder="Select talent" />
                        </SelectTrigger>
                        <SelectContent>
                          {talent.filter(e => e.isActive).map((talent) => (
                            <SelectItem key={talent.id} value={talent.id} data-testid={`option-talent-${talent.id}`}>
                              {talent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : col.type === "starred" ? (
                      <div className="spreadsheet-cell h-7 flex items-center justify-center border-r border-border">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateLeadMutation.mutate({ id: lead.id, updates: { starred: !(lead as any).starred } });
                          }}
                          className="p-0.5 rounded hover:bg-muted transition-colors"
                          data-testid={`button-star-lead-${lead.id}`}
                          title={(lead as any).starred ? "Unstar" : "Star"}
                        >
                          <Star
                            className={cn(
                              "w-4 h-4 transition-colors",
                              (lead as any).starred
                                ? "fill-amber-400 text-amber-400"
                                : "text-muted-foreground/30 hover:text-amber-300",
                            )}
                          />
                        </button>
                      </div>
                    ) : col.type === "rating" ? (
                      <div className="spreadsheet-cell h-7 flex items-center border-r border-border gap-1 pr-1">
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={10}
                          step={1}
                          className="flex-1 min-w-0 h-7 px-2 text-sm bg-transparent border-0 outline-none focus:ring-1 focus:ring-primary/50 rounded-none"
                          value={
                            (lead as any)[col.key] === null || (lead as any)[col.key] === undefined
                              ? ""
                              : String((lead as any)[col.key])
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              handleCellChange(lead.id, col.key, null);
                              return;
                            }
                            const n = parseInt(raw, 10);
                            if (Number.isNaN(n)) return;
                            const clamped = Math.max(0, Math.min(10, n));
                            handleCellChange(lead.id, col.key, clamped);
                          }}
                          placeholder="—"
                          data-testid={`input-rating-${lead.id}`}
                        />
                        {((lead as any).ratingSummary || (lead as any).ratingLabel || (lead as any).qualificationStatus) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setQualificationLeadId(lead.id); }}
                                className="flex-shrink-0 text-muted-foreground hover:text-primary"
                                title="View qualification breakdown"
                                data-testid={`button-qualification-${lead.id}`}
                              >
                                {(lead as any).qualificationStatus === "Processing" ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <HelpCircle className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="text-xs space-y-1">
                                {(lead as any).ratingLabel && (
                                  <div className="font-medium">{(lead as any).ratingLabel} ({(lead as any).rating}/5)</div>
                                )}
                                {(lead as any).ratingSummary && <div>{(lead as any).ratingSummary}</div>}
                                {(() => {
                                  // ratingSource is JSONB { signals, sources } —
                                  // never render the raw object. Show up to
                                  // 3 source titles instead.
                                  const rs: any = (lead as any).ratingSource;
                                  const arr: any[] = Array.isArray(rs?.sources) ? rs.sources : [];
                                  if (arr.length === 0) return null;
                                  return (
                                    <div className="text-muted-foreground italic">
                                      Sources: {arr.slice(0, 3).map((s: any) => s?.title || s?.url || "").filter(Boolean).join(", ")}
                                      {arr.length > 3 ? ` +${arr.length - 3} more` : ""}
                                    </div>
                                  );
                                })()}
                                <div className="text-[10px] text-muted-foreground">Click for full breakdown</div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    ) : col.type === "select" && col.options ? (
                      <Select
                        value={(lead as any)[col.key] || col.options[0]}
                        onValueChange={(value) => handleCellChange(lead.id, col.key, value)}
                      >
                        <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                          <SelectValue>
                            {(lead as any)[col.key] || col.options[0]}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {col.options.map((option: string) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : col.type === "user-select" ? (
                      <Select
                        value={(lead as any)[col.key] || (users[0] ? (users[0].firstName && users[0].lastName ? `${users[0].firstName} ${users[0].lastName}` : "") : "")}
                        onValueChange={(value) => handleCellChange(lead.id, col.key, value)}
                      >
                        <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                          <SelectValue placeholder="Select rep...">
                            {(lead as any)[col.key] || "Select rep..."}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {users.map((user) => {
                            const displayName = user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName || user.lastName || user.email || "Unknown User";
                            return (
                              <SelectItem key={user.id} value={displayName}>
                                {displayName}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    ) : col.type === "readonly" ? (
                      <div className="spreadsheet-cell h-7 flex items-center text-muted-foreground bg-muted/30">
                        {(lead as any)[col.key] || ""}
                      </div>
                    ) : col.type === "checkbox" ? (
                      <div className="spreadsheet-cell h-7 flex items-center justify-center border-r border-border">
                        <Checkbox
                          checked={(lead as any)[col.key] || false}
                          onCheckedChange={(checked) => handleCellChange(lead.id, col.key, checked === true)}
                          data-testid={`checkbox-${col.key}-${lead.id}`}
                        />
                      </div>
                    ) : col.key === "notes" && !(editingCell?.rowId === lead.id && editingCell?.column === "notes") ? (
                      // Notes cell: double-click opens full-text editor dialog; single click enters inline edit
                      (() => {
                        const raw = ((lead as any).notes || "").toString();
                        return (
                          <div
                            className="spreadsheet-cell h-7 flex items-center border-r border-border px-2 cursor-text overflow-hidden"
                            onDoubleClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedRowId(lead.id);
                              setEditingCell(null);
                              setEditNotesLeadId(lead.id);
                              setEditNotesValue(raw);
                            }}
                            onClick={() => {
                              setSelectedRowId(lead.id);
                              setEditingCell({ rowId: lead.id, column: "notes" });
                            }}
                            title="Double-click to open full editor"
                            data-testid={`cell-notes-${lead.id}`}
                          >
                            <span className={`text-sm truncate w-full ${raw ? "" : "text-muted-foreground/50"}`}>
                              {raw || "Add notes…"}
                            </span>
                          </div>
                        );
                      })()
                    ) : col.key === "website" && !(editingCell?.rowId === lead.id && editingCell?.column === "website") ? (
                      // Website cell: clickable link icon + text shortcut to edit
                      (() => {
                        const raw = ((lead as any).website || "").trim();
                        const href = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
                        return (
                          <div className="spreadsheet-cell h-7 flex items-center gap-1 border-r border-border px-2 group">
                            {raw ? (
                              <>
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary/80 flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                  title={`Open ${raw}`}
                                  data-testid={`link-website-${lead.id}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                                <button
                                  type="button"
                                  className="flex-1 min-w-0 text-left text-sm truncate text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    setSelectedRowId(lead.id);
                                    setEditingCell({ rowId: lead.id, column: "website" });
                                  }}
                                  data-testid={`button-edit-website-${lead.id}`}
                                >
                                  {raw}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="w-full h-full text-left text-sm text-muted-foreground/50 hover:text-foreground"
                                onClick={() => {
                                  setSelectedRowId(lead.id);
                                  setEditingCell({ rowId: lead.id, column: "website" });
                                }}
                                data-testid={`button-add-website-${lead.id}`}
                              >
                                Add website…
                              </button>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="relative">
                        <SpreadsheetCell
                          value={
                            col.key === "lastContacted"
                              ? ((lead as any)[col.key]
                                  ? formatLocalDate((lead as any)[col.key], { month: "short", day: "numeric" })
                                  : "")
                              : ((lead as any)[col.key] || "")
                          }
                          onChange={(value) => handleCellChange(lead.id, col.key, value)}
                          isEditing={editingCell?.rowId === lead.id && editingCell?.column === col.key}
                          onStartEdit={() => {
                            setSelectedRowId(lead.id);
                            setEditingCell({ rowId: lead.id, column: col.key });
                          }}
                          onEndEdit={() => setEditingCell(null)}
                          onTab={(shiftKey) => handleTabNavigation(lead.id, col.key, shiftKey)}
                          onFocus={() => setSelectedRowId(lead.id)}
                          type={col.type as any}
                          placeholder={col.label}
                          dialContext={col.type === "phone" ? { leadId: lead.id, contactName: (lead as any).contactName || (lead as any).leadName } : undefined}
                          showDial={col.type === "phone" ? twilioEnabled : undefined}
                        />
                        {col.key === "leadName" && (lead as any).quadrant && quadrantTagsEnabled && (
                          <span
                            className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-mono px-1 py-0 h-3.5 leading-none rounded border border-border/60 bg-background/90 text-muted-foreground flex items-center"
                            title={`${(lead as any).distanceMiles != null ? `${(lead as any).distanceMiles} mi ` : ""}${(lead as any).quadrant} of search center`}
                            data-testid={`badge-lead-quadrant-${lead.id}`}
                          >
                            {(lead as any).quadrant}
                          </span>
                        )}
                        {col.key === "emailAddress" &&
                          (lead as any).emailAddress &&
                          invalidEmailSet.has(((lead as any).emailAddress || "").toLowerCase()) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`badge-invalid-email-${lead.id}`}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-block w-2 h-2 rounded-full bg-destructive shrink-0 cursor-default"
                                />
                              </TooltipTrigger>
                              <TooltipContent>Invalid email — failed verification. Edit to clear.</TooltipContent>
                            </Tooltip>
                          )}
                        {/* Qualification rating badge hidden — parked for future enhancement */}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              );
            })}
            </div>

            {/* Infinite scroll sentinel — triggers fetchNextPage on the
                infinite query when it scrolls into view (rootMargin gives
                ~200px of pre-fetch). */}
            {hasMoreLeads && (
              <div ref={loadMoreSentinelRef} className="h-8 flex items-center justify-center text-xs text-muted-foreground" data-testid="leads-load-more-sentinel">
                {isFetchingNextPage ? "Loading more leads…" : "Scroll to load more"}
              </div>
            )}

            {/* Count indicator once the cursor is exhausted */}
            {!hasMoreLeads && sortedLeads.length > 50 && (
              <div className="h-6 flex items-center justify-center text-xs text-muted-foreground border-b border-border bg-muted/20">
                Showing all {sortedLeads.length} leads
              </div>
            )}

            {/* Empty rows for new entries — only the FIRST row is editable to
                prevent accidental double-creates (typing in row N then row N+1
                used to create two separate leads). Subsequent rows render as
                inert visual filler so the spreadsheet still feels full. */}
            {emptyRows.map((row, index) => {
              const isActiveEntryRow = index === 0;
              return (
                <div
                  key={row.id}
                  className={cn(
                    "flex border-b border-border transition-opacity",
                    isActiveEntryRow ? "opacity-60 hover:opacity-100" : "opacity-30 pointer-events-none",
                  )}
                >
                  <div className="w-8 shrink-0 h-7 flex items-center justify-center text-xs text-muted-foreground border-r border-border bg-muted/50">
                    {sortedLeads.length + index + 1}
                  </div>
                  {COLUMNS.map((col, colIndex) => (
                    <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}>
                      {!isActiveEntryRow || col.type === "region" || col.type === "readonly" || col.type === "select" || col.type === "user-select" || col.type === "talent" || col.type === "checkbox" || col.type === "rating" || col.type === "starred" ? (
                        <div className="h-7 border-r border-border" />
                      ) : (
                        <SpreadsheetCell
                          value=""
                          onChange={(value) => {
                            if (value && selectedRegions.length === 1) {
                              handleLeadCreateWithDuplicateCheck(selectedRegions[0], selectedClientType, col.key, value);
                            } else if (value && selectedRegions.length === 0) {
                              toast({ 
                                title: "Select a Region", 
                                description: "Please select a region before adding leads.",
                                variant: "destructive" 
                              });
                            } else if (value && selectedRegions.length > 1) {
                              toast({ 
                                title: "Select One Region", 
                                description: "Please select only one region to add a new lead.",
                                variant: "destructive" 
                              });
                            }
                          }}
                          isEditing={editingCell?.rowId === row.id && editingCell?.column === col.key}
                          onStartEdit={() => setEditingCell({ rowId: row.id, column: col.key })}
                          onEndEdit={() => setEditingCell(null)}
                          type={col.type as any}
                          placeholder={colIndex === 0 ? "Click to add lead..." : ""}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Add more rows button */}
            <div className="flex items-center justify-center py-1 border-b border-border bg-muted/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddMoreRows}
                className="text-muted-foreground"
                data-testid="button-add-more-rows"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add 10 rows
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Client Duplicate Warning Dialog */}
      <Dialog open={clientDuplicateOpen} onOpenChange={(open) => {
        if (!open) {
          setPendingLeadCreate(null);
          setPendingModalLeadData(null);
          setMatchedClients([]);
        }
        setClientDuplicateOpen(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-amber-500">⚠</span>
              {matchedClients.length === 1 ? "This Account May Already Be a Client" : "These Accounts May Already Be Clients"}
            </DialogTitle>
            <DialogDescription>
              {matchedClients.length === 1
                ? `A client already exists that matches the ${matchedClients[0]?.matchedFields.join(" or ")} you entered. Would you like to view them, or add this lead anyway?`
                : `${matchedClients.length} clients already exist that match the value you entered.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {matchedClients.map((client) => (
              <div key={client.id} className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{client.accountName || "(no name)"}</div>
                  <div className="flex gap-1">
                    {client.matchedFields.map((f, fi) => (
                      <span key={fi} className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded px-1.5 py-0.5">{f}</span>
                    ))}
                  </div>
                </div>
                {client.emailAddress && <div className="text-muted-foreground text-xs">{client.emailAddress}</div>}
                {client.phoneNumber && <div className="text-muted-foreground text-xs">{formatPhoneNumber(client.phoneNumber)}</div>}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setPendingLeadCreate(null);
                setPendingLeadUpdate(null);
                setPendingModalLeadData(null);
                setMatchedClients([]);
                setClientDuplicateOpen(false);
              }}
              data-testid="button-cancel-client-duplicate"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setMatchedClients([]);
                setClientDuplicateOpen(false);
                // Handle update path (editing existing lead)
                if (pendingLeadUpdate) {
                  executePendingLeadUpdate();
                  return;
                }
                // Handle modal create path
                if (pendingModalLeadData) {
                  const modalData = pendingModalLeadData;
                  setPendingModalLeadData(null);
                  // Still check for duplicate leads before creating
                  const checkFields: Array<{ fieldKey: string; value: string }> = [];
                  if (modalData.leadName?.trim()) checkFields.push({ fieldKey: "leadName", value: modalData.leadName.trim() });
                  if (modalData.emailAddress?.trim()) checkFields.push({ fieldKey: "emailAddress", value: modalData.emailAddress.trim() });
                  if (modalData.phoneNumber?.trim()) checkFields.push({ fieldKey: "phoneNumber", value: modalData.phoneNumber.trim() });
                  for (const { fieldKey, value } of checkFields) {
                    const leadMatches = findDuplicateLeads(fieldKey, value);
                    if (leadMatches.length > 0) {
                      setDuplicateMatchedLeads(leadMatches);
                      setSelectedMergeTargetId(leadMatches[0].id);
                      setPendingModalLeadData(modalData);
                      setDuplicateConfirmOpen(true);
                      return;
                    }
                  }
                  createLeadMutation.mutate({
                    regionId: modalData.regionId,
                    clientType: modalData.clientType || "",
                    leadName: modalData.leadName || undefined,
                    contactName: modalData.contactName || undefined,
                    phoneNumber: modalData.phoneNumber || undefined,
                    mobilePhone: modalData.mobilePhone || undefined,
                    emailAddress: modalData.emailAddress || undefined,
                  });
                  return;
                }
                // Handle create path (new lead from empty row)
                if (!pendingLeadCreate) return;
                const { regionId, clientType, fieldKey, value } = pendingLeadCreate;
                setPendingLeadCreate(null);
                // Still check for duplicate leads before creating
                const leadMatches = findDuplicateLeads(fieldKey, value);
                if (leadMatches.length > 0) {
                  setDuplicateMatchedLeads(leadMatches);
                  setSelectedMergeTargetId(leadMatches[0].id);
                  setPendingLeadCreate({ regionId, clientType, fieldKey, value });
                  setDuplicateConfirmOpen(true);
                  return;
                }
                createLeadMutation.mutate({ regionId, clientType }, {
                  onSuccess: (newLead: any) => {
                    if (newLead && newLead.id) {
                      updateLeadMutation.mutate({ id: newLead.id, updates: { [fieldKey]: value } });
                    }
                  }
                });
              }}
              data-testid="button-ignore-client-duplicate"
            >
              Ignore & Save Anyway
            </Button>
            <Button
              onClick={() => {
                setClientDuplicateOpen(false);
                setPendingLeadCreate(null);
                setPendingLeadUpdate(null);
                setPendingModalLeadData(null);
                setMatchedClients([]);
                navigate("/clients");
              }}
              data-testid="button-view-client-duplicate"
            >
              View Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Lead Confirmation Dialog */}
      <Dialog open={duplicateConfirmOpen} onOpenChange={(open) => {
        if (!open) {
          setPendingLeadCreate(null);
          setPendingLeadUpdate(null);
          setPendingModalLeadData(null);
          setDuplicateMatchedLeads([]);
          setSelectedMergeTargetId("");
        }
        setDuplicateConfirmOpen(open);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Similar Lead{duplicateMatchedLeads.length > 1 ? "s" : ""} Found</DialogTitle>
            <DialogDescription>
              {duplicateMatchedLeads.length === 1
                ? `A lead already exists that matches the ${duplicateMatchedLeads[0]?.matchedFields.join(" or ")} you entered.`
                : `${duplicateMatchedLeads.length} leads already exist that match the value you entered.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {duplicateMatchedLeads.map((match) => {
              const owner = users.find(u => u.id === match.createdBy);
              const ownerName = owner ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() : "Another team member";
              return (
                <div key={match.id} className={`rounded-lg border p-3 text-sm space-y-1 ${selectedMergeTargetId === match.id ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{match.leadName || "(no name)"}</div>
                    <div className="flex gap-1">
                      {match.matchedFields.map((f, fi) => (
                        <span key={fi} className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded px-1.5 py-0.5">{f}</span>
                      ))}
                    </div>
                  </div>
                  {match.emailAddress && <div className="text-muted-foreground text-xs">{match.emailAddress}</div>}
                  {match.phoneNumber && <div className="text-muted-foreground text-xs">{formatPhoneNumber(match.phoneNumber)}</div>}
                  <div className="text-muted-foreground text-xs">Created by: {ownerName}</div>
                  {duplicateMatchedLeads.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 h-6 text-xs"
                      onClick={() => setSelectedMergeTargetId(match.id)}
                      data-testid={`button-select-merge-target-${match.id}`}
                    >
                      {selectedMergeTargetId === match.id ? "Selected for merge" : "Select for merge"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setPendingLeadCreate(null);
                setPendingLeadUpdate(null);
                setPendingModalLeadData(null);
                setDuplicateMatchedLeads([]);
                setSelectedMergeTargetId("");
                setDuplicateConfirmOpen(false);
              }}
              data-testid="button-cancel-duplicate-lead"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDuplicateMatchedLeads([]);
                setSelectedMergeTargetId("");
                setDuplicateConfirmOpen(false);
                // If this was triggered from an update, execute the update
                if (pendingLeadUpdate) {
                  executePendingLeadUpdate();
                  return;
                }
                handleConfirmedLeadCreate();
              }}
              data-testid="button-confirm-duplicate-lead"
            >
              {pendingLeadUpdate ? "Save Anyway" : "Create Anyway"}
            </Button>
            {!pendingLeadUpdate && (
              <Button
                onClick={handleMergeLeadCreate}
                disabled={!selectedMergeTargetId}
                data-testid="button-merge-duplicate-lead"
              >
                Merge into Existing
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Edit Lead dialog — allows full field editing on mobile */}
      <Dialog open={isMobileEditOpen} onOpenChange={setIsMobileEditOpen}>
        <DialogContent className="w-full sm:max-w-md max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between gap-2 pr-6">
              <DialogTitle>Edit Lead</DialogTitle>
              {selectedRowId && (
                <CatchMeUpButton
                  entityType="lead"
                  entityId={selectedRowId}
                  eventCount={leadActivities.length}
                />
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="mobile-lead-name">Account Name</Label>
              <Input
                id="mobile-lead-name"
                value={mobileEditForm.leadName}
                onChange={e => setMobileEditForm(f => ({ ...f, leadName: e.target.value }))}
                placeholder="Account name..."
                data-testid="mobile-edit-lead-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile-contact-name">Contact Name</Label>
              <Input
                id="mobile-contact-name"
                value={mobileEditForm.contactName}
                onChange={e => setMobileEditForm(f => ({ ...f, contactName: e.target.value }))}
                placeholder="Contact name..."
                data-testid="mobile-edit-contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile-phone">Phone Number</Label>
              <Input
                id="mobile-phone"
                type="tel"
                value={mobileEditForm.phoneNumber}
                onChange={e => setMobileEditForm(f => ({ ...f, phoneNumber: e.target.value }))}
                placeholder="Phone number..."
                data-testid="mobile-edit-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile-email">Email Address</Label>
              <Input
                id="mobile-email"
                type="email"
                value={mobileEditForm.emailAddress}
                onChange={e => setMobileEditForm(f => ({ ...f, emailAddress: e.target.value }))}
                placeholder="Email address..."
                data-testid="mobile-edit-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile-notes">Notes</Label>
              <Textarea
                id="mobile-notes"
                value={mobileEditForm.notes}
                onChange={e => setMobileEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes..."
                rows={3}
                data-testid="mobile-edit-notes"
              />
            </div>
          </div>
          <DialogFooter className="shrink-0 flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => setIsMobileEditOpen(false)} data-testid="mobile-edit-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedRowId) {
                  updateLeadMutation.mutate({
                    id: selectedRowId,
                    updates: {
                      leadName: mobileEditForm.leadName,
                      contactName: mobileEditForm.contactName,
                      phoneNumber: mobileEditForm.phoneNumber,
                      emailAddress: mobileEditForm.emailAddress,
                      notes: mobileEditForm.notes,
                    }
                  });
                  setIsMobileEditOpen(false);
                }
              }}
              disabled={updateLeadMutation.isPending}
              data-testid="mobile-edit-save"
            >
              {updateLeadMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile action sheet — opens when a lead card is tapped */}
      <Sheet open={isMobileActionSheetOpen} onOpenChange={setIsMobileActionSheetOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-xl pb-safe">
          <SheetHeader className="pb-3 border-b border-border">
            <div className="flex items-center gap-3 text-left">
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-base font-semibold ${bsAvatarSwatch(selectedLead?.leadName || "Lead")}`}>
                {bsInitials(selectedLead?.leadName || "Lead")}
              </div>
              <div className="min-w-0">
                <SheetTitle className="truncate text-[17px] leading-tight">
                  {selectedLead?.leadName || "Lead Actions"}
                </SheetTitle>
                {selectedLead?.status && selectedLead.status !== "active" && (
                  <p className="mt-0.5 text-xs font-normal capitalize text-muted-foreground">{selectedLead.status.replace(/_/g, " ")}</p>
                )}
              </div>
            </div>
          </SheetHeader>
          <div className="py-3 space-y-1">
            {/* Lead info summary */}
            {selectedLead && (selectedLead.contactName || selectedLead.phoneNumber || selectedLead.emailAddress) && (
              <div className="mb-2 rounded-2xl border border-border bg-muted/30 p-3.5 space-y-2 text-sm">
                {selectedLead.contactName && (
                  <div className="flex items-center gap-2.5 text-muted-foreground">
                    <Eye className="h-4 w-4 shrink-0 text-muted-foreground/60" />
                    <span className="text-foreground">{selectedLead.contactName}</span>
                  </div>
                )}
                {selectedLead.phoneNumber && (
                  <div className="flex items-center gap-2.5 text-muted-foreground">
                    <Phone className="h-4 w-4 shrink-0 text-muted-foreground/60" />
                    <a href={`tel:${selectedLead.phoneNumber}`} className="text-primary" onClick={() => setIsMobileActionSheetOpen(false)}>{formatPhoneNumber(selectedLead.phoneNumber)}</a>
                  </div>
                )}
                {selectedLead.emailAddress && (
                  <div className="flex items-center gap-2.5 text-muted-foreground">
                    <Mail className="h-4 w-4 shrink-0 text-muted-foreground/60" />
                    <span className="truncate text-foreground">{selectedLead.emailAddress}</span>
                  </div>
                )}
              </div>
            )}

            {/* LeadQualificationCard (mobile) hidden — parked for future enhancement */}

            {/* Primary actions */}
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => {
                if (selectedLead) {
                  setMobileEditForm({
                    leadName: selectedLead.leadName || "",
                    contactName: selectedLead.contactName || "",
                    phoneNumber: selectedLead.phoneNumber || "",
                    emailAddress: selectedLead.emailAddress || "",
                    notes: selectedLead.notes || "",
                  });
                  setIsMobileActionSheetOpen(false);
                  setIsMobileEditOpen(true);
                }
              }}
              data-testid="mobile-action-edit-lead"
            >
              <Pencil className="h-5 w-5 text-muted-foreground" /> Edit Lead Details
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsEmailDialogOpen(true); }}
              disabled={!selectedLead?.emailAddress}
              data-testid="mobile-action-email"
            >
              <Mail className="h-5 w-5 text-muted-foreground" /> Email Lead
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsLogCallDialogOpen(true); }}
              data-testid="mobile-action-log-call"
            >
              <Phone className="h-5 w-5 text-muted-foreground" /> Log Call
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsSetReminderDialogOpen(true); }}
              data-testid="mobile-action-set-reminder"
            >
              <Bell className="h-5 w-5 text-muted-foreground" /> Set Reminder
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base gap-3"
              onClick={() => { setIsMobileActionSheetOpen(false); setIsViewDetailsDialogOpen(true); }}
              data-testid="mobile-action-view-activity"
            >
              <History className="h-5 w-5 text-muted-foreground" /> View Activity
            </Button>

            {/* Status changes */}
            <div className="border-t pt-2 mt-1">
              <p className="px-1 pb-1 text-xs text-muted-foreground font-medium uppercase tracking-wide">Set Status</p>
              <Button
                variant="ghost"
                className="w-full justify-start h-11 gap-3"
                onClick={() => { if (selectedRowId) { updateLeadStatusMutation.mutate({ id: selectedRowId, status: "booked" }); } setIsMobileActionSheetOpen(false); }}
                data-testid="mobile-action-book"
              >
                <Calendar className="h-4 w-4 text-primary" /> Booked
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-11 gap-3"
                onClick={() => { if (selectedRowId) { updateLeadStatusMutation.mutate({ id: selectedRowId, status: "interested" }); } setIsMobileActionSheetOpen(false); }}
                data-testid="mobile-action-interested"
              >
                <Star className="h-4 w-4 text-yellow-500" /> Interested
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-11 gap-3"
                onClick={() => { if (selectedRowId) { updateLeadStatusMutation.mutate({ id: selectedRowId, status: "not_now" }); } setIsMobileActionSheetOpen(false); }}
                data-testid="mobile-action-not-now"
              >
                <Clock className="h-4 w-4 text-orange-500" /> Not Now
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-11 gap-3 text-destructive hover:text-destructive"
                onClick={() => { if (selectedRowId) { updateLeadStatusMutation.mutate({ id: selectedRowId, status: "disqualified" }); } setIsMobileActionSheetOpen(false); }}
                data-testid="mobile-action-disqualify"
              >
                <XCircle className="h-4 w-4" /> Disqualify
              </Button>
            </div>

            {/* Danger zone */}
            <div className="border-t pt-2 mt-1">
              <Button
                variant="ghost"
                className="w-full justify-start h-11 gap-3 text-destructive hover:text-destructive"
                onClick={() => { if (selectedRowId) setConfirmDeleteLeadId(selectedRowId); setIsMobileActionSheetOpen(false); }}
                data-testid="mobile-action-delete"
              >
                <Trash2 className="h-4 w-4" /> Delete Lead
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* QualificationBreakdownDialog hidden — parked for future enhancement */}

      <AddLeadModal
        open={isAddLeadModalOpen}
        onOpenChange={setIsAddLeadModalOpen}
        regions={regions}
        clientTypeOptions={clientTypeOptions}
        onSubmit={handleAddLeadModalSubmit}
        isPending={createLeadMutation.isPending}
        defaultRegionId={selectedRegions.length === 1 ? selectedRegions[0] : (regions[0]?.id ?? "")}
      />
    </div>
  );
}
